/**
 * Locket photo-upload personalization (Phase 1 -> 1.7).
 *
 * A plain custom element (no theme Component coupling) that:
 *   - takes a photo upload, decoding HEIC (native first, vendored lib as a
 *     lazy fallback only on browsers that cannot),
 *   - lets the buyer pan and zoom it inside a heart/oval aperture,
 *   - enforces a minimum crop resolution with a friendly message,
 *   - writes the buyer's original file, byte for byte, + a crop transform
 *     into hidden form-associated inputs so they ride the normal add-to-cart
 *     FormData,
 *   - gates the add-to-cart button until a crop and the rights checkbox are done.
 *
 * Phase 1.5 upgrades the preview to a true in-locket composite (the buyer's
 * crop seated into an open-locket base photo). Phase 1.6 injects that composite
 * into the product media gallery as a "Your preview" slide + lightbox.
 *
 * Phase 1.7 adds the SECOND photo (the other locket face) as a paid add-on:
 *   - offered only when the product's aperture data defines two windows,
 *   - the second photo's file + crop ride a SEPARATE add-on product line, so
 *     deleting that line removes the photo and the charge together, and there
 *     is no cart state where a customer pays without getting or gets without
 *     paying (a global reconciler removes an add-on orphaned by a removed
 *     locket; see assets/mab-locket-reconcile.js),
 *   - the composite and the gallery slide render BOTH photos in their windows,
 *   - and the counter on the injected slide is corrected to read N/N.
 *
 * The uploaded photo is the buyer's original, the File the picker produced,
 * with no crop, resize or re-encode (changed by the 4 Oct kickoff: originals on
 * the order). _crop.nat is that original's upright size, EXIF orientation
 * applied, and _crop.rect is a fraction of it, so a print tool that applies the
 * orientation the same way lands the crop on the same pixels. The one exception
 * is a HEIC that Shopify refuses: that add is resent once with a
 * full-resolution JPEG and `_Photo_source: heic-converted` on the line (see
 * retryHeicAsJpeg). The crop box itself draws a working copy scaled to 1500 px,
 * which never leaves the page. The print-sheet
 * generator re-derives the crop from _Photo and _crop.rect (and _Photo2 and
 * _crop2.rect) and never takes a print dimension from _crop, aperture_mm
 * included. Both file properties
 * lead with an underscore, which is what keeps the customer's filename out of
 * the cart and the checkout while leaving the file on the order in the admin. _crop.aspect is a screen
 * preview ratio, not an aperture measurement. The only millimetres in _crop are
 * aperture_mm, written for a measured locket alone and echoing the theme's
 * shape registry (mab-locket-shapes.js); a printed size still comes from Locket
 * Print Studio's own shape registry, or from a measurement the owner types into
 * that tool, and never from a Shopify metafield, because that tool reads no
 * Shopify data at all.
 *
 * The photo machinery itself, the file checks, the HEIC fallback, the decode,
 * the working copy, the resolution guard, the crop maths and the _crop
 * payload, lives in mab-photo-core.js, shared with the locket prints page, so
 * the two surfaces cannot drift apart. This file keeps the product page: its
 * steps, its sheet, its gallery and its add to cart.
 */

import {
  MIN_CROP_PX,
  buildCrop,
  centredView,
  clampView,
  coverScale,
  cropFromView,
  decode,
  fullResJpeg,
  heicToJpeg,
  isHeicFile,
  isSvgFile,
  isTooSmall,
  matchesNat,
  normaliseRect,
  notPhotoMessage,
  paintView,
  pinchZoom,
  round4,
  shapePath,
  tooSmallMessage,
  wheelZoom,
  workingCopy,
  zoomView,
} from '@theme/mab-photo-core';
import { locketShape } from '@theme/mab-locket-shapes';

const APERTURE_KEYS = ['cx', 'cy', 'w', 'h', 'rot', 'shadow', 'feather'];
// What _Photo_source and _Photo2_source say on a line whose HEIC was refused
// and resent as a JPEG. Written only by applySourceInputs, and only for an
// upload that resendAsJpeg swapped in.
const SOURCE_CONVERTED = 'heic-converted';
const CART_ADD_URL = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';

class MabLocketUpload extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    // Which locket this is, by handle, in the theme's shape registry. The
    // registry decides the shape and whether the locket is measured; the
    // custom.locket_shape metafield Liquid hands over is the fallback for a
    // handle the registry does not know yet. The registry's aspect is not read
    // here: this page's crop box follows the window drawn over the base photo
    // (windowAspect), and a box of any other ratio squeezes the face.
    this.handle = this.dataset.handle || '';
    this.shapeEntry = locketShape(this.handle);
    this.shape = (this.shapeEntry && this.shapeEntry.shape) || this.dataset.shape;
    if (this.shapeEntry && this.dataset.shape && this.shapeEntry.shape !== this.dataset.shape) {
      // The mask Liquid drew follows the metafield, so a disagreement shows a
      // heart over an oval preview. Loud in the console; the owner fixes the
      // metafield or the registry, whichever is wrong.
      console.error('[mab-locket] registry shape', this.shapeEntry.shape, 'disagrees with locket_shape', this.dataset.shape);
    }
    this.minPx = parseInt(this.dataset.minPx, 10) || MIN_CROP_PX;
    this.aspectW = parseFloat(this.dataset.aspectW) || 1;
    this.aspectH = parseFloat(this.dataset.aspectH) || 1;
    this.formId = this.dataset.formId;
    this.productId = this.dataset.productId || '';
    this.addonVariant = this.dataset.addonVariant || '';

    // Refs (plain [ref] attribute lookups, no framework dependency).
    this.stage = this.q('stage');
    this.fileInput = this.q('fileInput');
    this.picker2 = this.q('picker2');
    this.fileInput2 = this.q('fileInput2');
    this.cropInput = this.q('cropInput');
    this.cropInput2 = this.q('cropInput2');
    // Only ever enabled on a line whose HEIC was refused and resent as a JPEG.
    this.sourceInput = this.q('sourceInput');
    this.sourceInput2 = this.q('sourceInput2');
    this.countInput = this.q('countInput');
    this.pairInput = this.q('pairInput');
    this.errorEl = this.q('error');
    this.statusEl = this.q('status');
    this.undoEl = this.q('undo');
    this.undoText = this.q('undoText');
    this.helper = this.q('helper');
    this.pick = this.q('pick');
    this.card = this.querySelector('.mab-locket__card');
    this.cropper = this.q('cropper');
    this.viewport = this.q('viewport');
    this.cropCanvas = this.q('cropCanvas');
    this.bleedCanvas = this.q('bleedCanvas');
    this.previewCanvas = this.q('previewCanvas');
    this.thumbCanvas = { 1: this.q('thumbCanvas1'), 2: this.q('thumbCanvas2') };
    this.zoomInput = this.q('zoom');
    this.applyBtn = this.q('applyCrop');
    this.offerBtn = this.q('offer');
    this.rights = this.q('rights');

    // Phase 1.5 refs.
    this.compositeStage = this.q('compositeStage');
    this.compositeCanvas = this.q('compositeCanvas');
    this.tuner = this.q('tuner');
    this.tunerJson = this.q('tunerJson');
    this.tunerCopy = this.q('tunerCopy');

    // Slice 2 refs: the three steps, the phone sheet and its bottom bar.
    this.photoUI = this.q('photoUI');
    this.inlineHome = this.q('inlineHome');
    this.flow = this.q('flow');
    this.stepsList = this.q('steps');
    this.sheet = this.q('sheet');
    this.sheetBody = this.q('sheetBody');
    this.sheetTitle = this.q('sheetTitle');
    this.sheetTrigger = this.q('sheetTrigger');
    this.sheetClose = this.q('sheetClose');
    this.stepStatus = this.q('stepStatus');
    this.barAction = this.q('barAction');
    this.barRights = this.q('barRights');
    this.rightsRow = this.q('rightsRow');
    this.rightsHome = this.q('rightsHome');
    this.stateNote = this.q('stateNote');
    this.modePhoto = this.q('modePhoto');
    this.modeBlank = this.q('modeBlank');
    this.barPrice = this.q('barPrice');
    this.priceStatus = this.q('priceStatus');
    this.shapeInput = this.q('shapeInput');
    this.locketPhotoInput = this.q('locketPhotoInput');
    this.noticeEl = this.q('notice');
    this.step = 1;
    this.mode = 'photo';

    // Per-slot working state. Slot 1 is the front face (rides the buy form as
    // Photo/_crop); slot 2 is the back face (rides the add-on line).
    this.photos = { 1: makeSlot(), 2: makeSlot() };
    this.editing = 1; // which slot the cropper is currently editing
    this.pairId = null; // joins the locket line to its second-photo add-on line

    this.tuningEnabled =
      this.dataset.tune === 'true' || /(?:^|[?&])locket_tune=1(?:&|$)/.test(location.search);
    if (this.tuningEnabled) this.dataset.tuning = 'on';

    this.parseAperture();
    // The base photo's pixel size, handed over by Liquid so the crop box is the
    // right shape before anything is fetched. Blank for a source Liquid cannot
    // measure, such as the bundled placeholder SVG; loadBase fills it in then.
    this.baseDims = {
      w: parseFloat(this.dataset.baseWidth) || 0,
      h: parseFloat(this.dataset.baseHeight) || 0,
    };
    // The open-locket base photo is 1400px wide and about 217KB, and it is only
    // ever drawn once there is a photo to seat in it, so the page does not
    // fetch it on load. The owner's tuner is the one case that needs it with no
    // photo at all.
    this.baseReady = Promise.resolve(false);
    this.baseImg = null;
    if (this.tuningEnabled) this.ensureBase();

    // Gallery injection is allowed ONLY for a real per-product base image, never
    // the demo placeholder or the bezel fallback (scope rule, keeps gate 25b).
    this.galleryEnabled = !!this.dataset.baseImage && this.dataset.basePlaceholder === 'false';
    if (this.galleryEnabled) this.initGallery();

    // Events.
    this.setupPick();
    this.fileInput.addEventListener('change', () => this.onPick(1));
    if (this.picker2) this.picker2.addEventListener('change', () => this.onPick(2));
    this.zoomInput.addEventListener('input', () => this.onZoom());
    this.applyBtn.addEventListener('click', () => this.applyCrop());

    // Replace / Remove / Edit / Add-second are bound by intent + slot, because
    // each appears in more than one place.
    this.querySelectorAll('[data-act]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const slot = btn.dataset.slot ? parseInt(btn.dataset.slot, 10) : this.editing;
        this.act(btn.dataset.act, slot);
      });
    });

    if (this.rights) this.rights.addEventListener('change', () => this.updateGate());

    /**
     * The native `required` on the file input is the hard backstop, and it has
     * one bad manner: the input is visually hidden, so a browser that reaches
     * constraint validation cannot focus it to report on, and refuses the
     * submit with nothing but a console warning. The capture handler speaks
     * first on every path we know of, but this makes the backstop say something
     * on any path we do not.
     */
    this.fileInput.addEventListener('invalid', (event) => {
      event.preventDefault();
      // The browser refused to submit, so nothing was sent and nothing is
      // coming back. This is the one place it is safe to say an add is over
      // without hearing from the cart, and the pending line has to go or it
      // sits in the drawer for a request that never happened.
      this._pendingAdd = false;
      this._sheetAdd = false;
      this.closeCartDrawer();
      this.removePendingLine();
      this.showError(this.dataset.msgNeedPhoto);
      this.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'center' });
    });

    this.setupPointer();
    this.setupTuner();
    this.setupSheet();
    this.setupChoices();
    this.setupMode();

    this._resizeTimer = 0;
    this._onWindowResize = () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this.onResize(), 150);
    };
    window.addEventListener('resize', this._onWindowResize);

    this.bindAddToCart();
    this.updateSecondUI();
  }

  /**
   * Five listeners live somewhere other than this element: three on `document`,
   * one on `window` and one on a media query list. All five outlive this element
   * unless they are taken off. A section re-render in the theme editor replaces
   * the block without reloading the page, and each replacement would otherwise
   * leave a dead copy of this element holding a capture handler on every click
   * in the document, a resize handler on every window resize, and a media query
   * handler, each of them keeping the closure and its full-resolution
   * ImageBitmaps alive. Nudging the padding slider ten times used to strand ten
   * of them.
   */
  disconnectedCallback() {
    if (this._onAddClickCapture) document.removeEventListener('click', this._onAddClickCapture, true);
    if (this._onCartUpdate) document.removeEventListener('cart:update', this._onCartUpdate);
    if (this._onCartError) document.removeEventListener('cart:error', this._onCartError);
    if (this._onVariantUpdate) document.removeEventListener('variant:update', this._onVariantUpdate);
    if (this._onWindowResize) {
      clearTimeout(this._resizeTimer);
      window.removeEventListener('resize', this._onWindowResize);
    }
    if (this.phone && this._onBreakpoint) {
      if (this.phone.removeEventListener) this.phone.removeEventListener('change', this._onBreakpoint);
      else if (this.phone.removeListener) this.phone.removeListener(this._onBreakpoint);
    }
    this.dropRemoved();
  }

  q(name) {
    return this.querySelector(`[ref="${name}"]`);
  }

  cur() {
    return this.photos[this.editing];
  }

  /* ---- state A: the drop card -------------------------------------------- */

  // The whole card is the target: a click opens the picker, a dropped file
  // enters the same pipeline as a picked one. dragenter/dragleave are counted
  // because they also fire for the card's own children.
  /**
   * Step 1 is one button and it opens the picker. There is no drop card, no
   * caption and no bar action behind it: the owner asked for one control and
   * one screen, and the phone's own photo picker is what the button is for.
   *
   * Drag and drop survives as a silent extra for a mouse. The target is the
   * whole panel rather than a bordered box that advertises itself, nothing in
   * the copy mentions dragging, and the only feedback is the card's edge while
   * a file is genuinely over it.
   */
  setupPick() {
    if (this.pick) {
      this.pick.addEventListener('click', () => {
        this.editing = 1;
        this.fileInput.click();
      });
    }

    const zone = this.card;
    if (!zone) return;
    let depth = 0;
    const setDragging = (on) => {
      this.dataset.drag = on ? 'true' : 'false';
    };

    // Only step 1 takes a drop: on step 2 or step 3 a photo is already placed,
    // and an accidental drag must not disturb it. The events are cancelled at
    // every step all the same. A file drop the page does not cancel is a
    // navigation to that file, which would take the buyer off the product page
    // and lose the crop she had, so the panel refuses the drop rather than
    // ignoring it, and says so with the cursor.
    const takesDrop = () => this.step === 1 && !this.blankMode;

    zone.addEventListener('dragenter', (e) => {
      e.preventDefault();
      if (!takesDrop()) return;
      depth += 1;
      setDragging(true);
    });
    zone.addEventListener('dragover', (e) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = takesDrop() ? 'copy' : 'none';
    });
    zone.addEventListener('dragleave', (e) => {
      e.preventDefault();
      depth = Math.max(0, depth - 1);
      if (depth === 0) setDragging(false);
    });
    zone.addEventListener('drop', (e) => {
      e.preventDefault();
      depth = 0;
      setDragging(false);
      if (!takesDrop()) return;
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) this.handleFile(file, 1);
    });
  }

  /* ---- Chain and Photo ---------------------------------------------------
     Two options crossed into six variants, resolved here rather than by asking
     the server for a re-rendered section. A `?variant=` round trip would morph
     this block away and take the buyer's photo, their crop and their ticked
     consent with it, which is the whole reason the map is printed into the page
     and read from here. */

  setupChoices() {
    // A list of {id, options}, compared element by element. The first version
    // keyed an object on the option values joined with ' / ', which is the
    // separator Shopify itself uses in a variant title; an owner who lists a
    // chain as '16 in / 40 cm' splits one value into two, shifts every row by
    // one, and gets a page whose chips mark nothing and whose price never
    // moves. There is no separator here to collide with.
    this.variantRows = safeJSON(this.dataset.variantMap) || [];
    if (!Array.isArray(this.variantRows)) this.variantRows = [];
    this.photoWith = this.dataset.photoWith || '';
    this.photoWithout = this.dataset.photoWithout || '';
    this.choiceGroups = Array.from(this.querySelectorAll('[data-choice-group]'));
    this.photoGroup =
      this.dataset.photoCoupled === 'true'
        ? this.choiceGroups.find((g) => g.dataset.choiceRole === 'photo') || null
        : null;

    // An option can be real and still have no row: a Photo option this block
    // cannot read draws none, on purpose. Those positions are pinned to the
    // variant the page opened on, so the lookup stays exact instead of matching
    // whichever variant happens to come first.
    const opened = this.variantRows.find((row) => String(row.id) === String(this.dataset.pendingVariant));
    this.baseOptions = (opened && opened.options) || [];
    // The picture the variant the page opened on carries, so the first
    // `syncVariant`, which runs on load, does not turn a gallery the buyer has
    // not touched.
    this.variantMedia = mediaOf(opened);

    this.choiceGroups.forEach((group) => {
      group.addEventListener('click', (event) => {
        const chip = event.target.closest('[data-choice-value]');
        if (!chip || !group.contains(chip)) return;
        this.selectChoice(group, chip.dataset.choiceValue);
      });
      group.addEventListener('keydown', (event) => this.onChoiceKeydown(group, event));
    });
  }

  chips(group) {
    return Array.from(group.querySelectorAll('[data-choice-value]'));
  }

  markedValue(group) {
    const chip = group.querySelector('[data-choice-value][aria-checked="true"]');
    return chip ? chip.dataset.choiceValue : '';
  }

  /**
   * The DOM half, and only the DOM half: which chip is checked, which one holds
   * the group's single tab stop, and what the header reads. Nothing here
   * changes the mode or the variant, so `applyMode` can call it without the two
   * of them calling each other forever.
   */
  markChoice(group, value) {
    // The tab stop belongs to whoever the buyer is on, which is usually the
    // checked chip and after a tap or an Enter is the same chip. The arrow keys
    // move focus without choosing though, so a photo placed while focus is
    // parked further along the row must not drag the tab stop out from under
    // it. Focus wins where the two disagree; the check mark is unaffected.
    const active = document.activeElement;
    const focused = active && group.contains(active) ? active.closest('[data-choice-value]') : null;
    this.chips(group).forEach((chip) => {
      const on = chip.dataset.choiceValue === value;
      chip.setAttribute('aria-checked', on ? 'true' : 'false');
      chip.tabIndex = (focused ? chip === focused : on) ? 0 : -1;
    });
    const label = group.querySelector('[data-choice-value-label]');
    if (label) label.textContent = value;
  }

  /**
   * A buyer choosing a value. The photo group goes through `setMode`, which is
   * the one place that decides what a blank locket carries, and `setMode` marks
   * the chip on the way back. Every other group marks itself and resolves.
   */
  selectChoice(group, value) {
    if (!value || value === this.markedValue(group)) return;
    if (group === this.photoGroup) {
      this.setMode(value === this.photoWithout ? 'blank' : 'photo');
      return;
    }
    this.markChoice(group, value);
    this.syncVariant();
  }

  /**
   * Arrows move within the group and choose as they go, which is what a radio
   * group does natively and what `role="radio"` promises. Focus moves before the
   * mark, and without scrolling: focusing a chip in a row that scrolls sideways
   * would otherwise jump the row under the buyer's thumb.
   */
  onChoiceKeydown(group, event) {
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    const chips = this.chips(group);
    if (chips.length < 2) return;
    // Index from the chip the buyer is ON, not the one that is checked. While
    // selection followed focus those were the same chip and either read worked.
    // Now that they can part company, indexing from the checked one walks back
    // to the selection on every press and the row cannot be crossed.
    const here = event.target && event.target.closest ? event.target.closest('[data-choice-value]') : null;
    const at = here ? chips.indexOf(here) : chips.findIndex((c) => c.getAttribute('aria-checked') === 'true');
    const from = at === -1 ? 0 : at;
    let next = from;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = chips.length - 1;
    else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (from + 1) % chips.length;
    else next = (from - 1 + chips.length) % chips.length;
    event.preventDefault();
    this.focusChip(group, chips[next]);
  }

  /**
   * Moves the group's single tab stop to the chip the buyer is on, and chooses
   * nothing. A chip is committed with Enter or Space, which a `type="button"`
   * raises as a click of its own, so the click handler stays the only place
   * that selects and the two paths cannot drift apart.
   */
  focusChip(group, chip) {
    this.chips(group).forEach((c) => {
      c.tabIndex = c === chip ? 0 : -1;
    });
    chip.focus({ preventScroll: true });
  }

  chosenValues() {
    return this.choiceGroups
      .slice()
      .sort((a, b) => Number(a.dataset.choicePosition) - Number(b.dataset.choicePosition))
      .map((group) => this.markedValue(group));
  }

  /**
   * The chosen values to a variant, and the variant to every number on the page
   * that quotes it. The id goes into the buy form's own input, which is what
   * `requestSubmit` sends and what the pending drawer line and the free shipping
   * meter already read, so one write moves all of them.
   *
   * No event is dispatched on that input on purpose. Horizon's variant picker
   * answers a change on it by fetching the section and morphing it, which is
   * exactly the re-render this design exists to avoid.
   */
  /**
   * The chosen values to a variant row, by position. Every option the product
   * has must agree: the ones with a row take the marked chip, the ones without
   * take whatever the page opened on.
   */
  resolveVariant() {
    const byPosition = new Map();
    this.choiceGroups.forEach((group) => {
      byPosition.set(Number(group.dataset.choicePosition) - 1, this.markedValue(group));
    });
    return (
      this.variantRows.find((row) => {
        if (!Array.isArray(row.options)) return false;
        return row.options.every((value, index) =>
          byPosition.has(index) ? byPosition.get(index) === value : this.baseOptions[index] === value
        );
      }) || null
    );
  }

  syncVariant() {
    if (!this.choiceGroups || !this.choiceGroups.length) return;
    const hit = this.resolveVariant();
    if (!hit) {
      // A miss is a state this page cannot be in: the chips move, the price
      // does not, and nothing says so until an order arrives quoting a number
      // nobody was shown. Loud in the console rather than silent.
      console.error('[mab-locket] no variant for', this.chosenValues(), 'in', this.variantRows.length, 'variants');
      return;
    }
    const id = String(hit.id);
    this.dataset.pendingVariant = id;
    const input = this.form && this.form.querySelector('input[name="id"]');
    if (input) input.value = id;

    const price = (safeJSON(this.dataset.pendingPrices) || {})[id] || '';
    if (price) {
      if (this.barPrice) this.barPrice.textContent = price;
      this.syncPagePrice(price);
      this.syncStickyBar(id, price);
      this.announcePrice(price);
    }
    this.showVariantMedia(hit);
    this.syncSheet();
  }

  /**
   * A chip that lands on a variant with a picture of its own turns the gallery
   * to that picture, the way Horizon does on a variant change, and nothing
   * else moves. Horizon does it by fetching the section and swapping the whole
   * gallery for the new one, which would take the buyer's preview slide with
   * it; this walks the slideshow already on the page to the slide holding that
   * media and selects it.
   *
   * Only when the picture changes from the last variant's. `syncVariant` runs
   * on load, on the photo chips and on every photo placed, and a gallery that
   * jumped back on each of those would yank the buyer off whatever slide she
   * was looking at. Compared with the previous variant's media, not with the
   * slide on screen, so leaving a variant with a picture for one without and
   * coming back turns the gallery again.
   *
   * A variant with no picture leaves the gallery where it is. So does a
   * picture the gallery does not hold, which is what the gallery's "hide other
   * variants' media" setting makes of every variant image but the first.
   */
  showVariantMedia(row) {
    const media = mediaOf(row);
    const previous = this.variantMedia;
    this.variantMedia = media;
    if (media === null || media === previous) return;
    const section = this.closest('.shopify-section');
    const gallery = (section && section.querySelector('media-gallery')) || this.galleryEl;
    const slideshow = gallery && gallery.querySelector('slideshow-component');
    if (!slideshow || typeof slideshow.select !== 'function') return;
    // `slides` is the list `select` counts in: the visible slides, with any
    // hidden by the theme left out, so an index read from it is the index
    // `select` means.
    const slides = slideshow.slides || [];
    const index = slides.findIndex((slide) => slide.querySelector(`[data-media-id="${media}"]`));
    if (index < 0) return;
    // Instant: an animated jump of more than one slide reorders the slides
    // in the scroller while it runs, and the preview slide lives in there too.
    // `select` already skips the scroll animation for reduced motion.
    slideshow.select(index, undefined, { animate: false });
  }

  /**
   * The sticky bar at the bottom of a phone is the price a buyer actually acts
   * on, and it is not a `product-price` element, so `syncPagePrice` never
   * touched it. It updated only on `variant:update`, which this design does not
   * dispatch because Horizon answers that by fetching and morphing the section
   * this block lives in. Left alone it kept quoting the variant the page loaded
   * on: the bar read $40 while the button beside it bought $75.
   */
  syncStickyBar(id, price) {
    document.querySelectorAll('sticky-add-to-cart').forEach((bar) => {
      if (bar.dataset.productId && String(bar.dataset.productId) !== String(this.productId)) return;
      bar.querySelectorAll('.sticky-add-to-cart__price .price:not(.compare-at-price)').forEach((node) => {
        node.textContent = price;
      });
      const title = bar.querySelector('.sticky-add-to-cart__variant');
      if (title) title.textContent = this.chosenValues().filter(Boolean).join(' / ');
      bar.dataset.currentVariantId = id;
    });
  }

  /**
   * "The price drops by $10 where they can see it happen" has no meaning for a
   * buyer who is not looking at it. The bar, the page price and the sticky bar
   * all change silently; this is the one place that says so out loud. Cleared
   * first so the same price twice is still announced, and debounced so an arrow
   * key sweep across three chains does not queue three sentences.
   */
  announcePrice(price) {
    if (!this.priceStatus) return;
    const line = `${this.chosenValues().filter(Boolean).join(', ')}, ${price}`;
    if (this._priceSaid === line) return;
    this._priceSaid = line;
    clearTimeout(this._priceTimer);
    this.priceStatus.textContent = '';
    this._priceTimer = setTimeout(() => {
      this.priceStatus.textContent = line;
    }, 350);
  }

  /**
   * The price the page itself shows, which is Horizon's own price block and not
   * ours. Without this the bar and the buy button follow the chain while the
   * number at the top of the page quotes whichever variant the page loaded on,
   * and the buyer reads the stale one first.
   *
   * Scoped to this product's own price block, because the recommendations below
   * carry price blocks of their own and they are quoting other products.
   */
  syncPagePrice(text) {
    const id = this.productId;
    document
      .querySelectorAll('product-price[data-product-id="' + id + '"] .price:not(.compare-at-price)')
      .forEach((node) => {
        node.textContent = text;
      });
  }

  /**
   * Placing a photo puts the order on the variant that charges for one, and
   * removing it takes the order back off it. The price moves where the buyer
   * can see it because the bar, the page price and the pending drawer line all
   * read the variant this sets.
   */
  choosePhoto(on, options) {
    if (!this.photoGroup) return;
    this.setMode(on ? 'photo' : 'blank', options);
  }

  /* ---- photo or blank ----------------------------------------------------
     A locket sent blank is a real thing to want, so it is a choice rather than
     a dead end. The two modes differ only in which inputs are disabled. */

  setupMode() {
    // A browser that restores form state on a reload or a back gives us a
    // checked blank radio and a photo-mode element, so the radio is the truth
    // about which mode the buyer is in, not the default.
    // With a Photo option on the product the chip row is the control and the
    // radios are not rendered at all, so the marked chip is the truth. Without
    // one, a browser restoring form state on a reload or a back gives us a
    // checked blank radio and a photo-mode element, and the radio is the truth
    // rather than the default.
    if (this.photoGroup) {
      this.mode = this.markedValue(this.photoGroup) === this.photoWithout ? 'blank' : 'photo';
    } else {
      this.mode = this.modeBlank && this.modeBlank.checked ? 'blank' : 'photo';
    }
    [this.modePhoto, this.modeBlank].forEach((input) => {
      if (input) input.addEventListener('change', () => this.setMode(input.value));
    });
    this.applyMode();
  }

  get blankMode() {
    return this.mode === 'blank';
  }

  setMode(mode, options) {
    this.mode = mode === 'blank' ? 'blank' : 'photo';
    this.hideError();
    this.applyMode(options);
    this.updateGate();
  }

  /**
   * Disabled, never merely un-required. A hidden checked box still submits and
   * a file input keeps its file, so clearing `required` would leave a photo and
   * a consent attached to an order that says blank. The values are left where
   * they are, so a buyer who changes her mind back finds her crop still there.
   */
  applyMode(options) {
    const { keepStash } = options || {};
    const blank = this.blankMode;
    this.dataset.mode = this.mode;
    this.fileInput.disabled = blank;
    this.cropInput.disabled = blank;
    if (this.rights) this.rights.disabled = blank;
    if (this.locketPhotoInput) this.locketPhotoInput.disabled = !blank;
    // A pair token on a blank order would join it to a paid second photo line,
    // which is exactly what the reconciler would then try to keep alive.
    if (this.pairInput) this.pairInput.disabled = blank;
    // The print tool cuts an insert from this. A blank locket has nothing to
    // cut, so a no-photo line carrying a shape is an instruction to print
    // something that does not exist.
    if (this.shapeInput) this.shapeInput.disabled = blank;
    // Blank means blank: there is no photo to put back, so nothing is held.
    //
    // Except when the buyer has just pressed Remove. `reset` stashes the photo
    // for Undo and then puts the order back on the no-photo variant, and that
    // second step used to come back through here and throw the stash away
    // before `reset` had finished with it: the Undo row flashed and vanished,
    // the announcement never fired because it was guarded on the stash, focus
    // fell to the body, and the crop was gone for good. Remove had been
    // undoable before this slice.
    if (blank && !keepStash) this.dropRemoved();
    this.applySecondInputs();
    const note = (blank ? this.dataset.noteBlank : this.dataset.notePhoto) || '';
    if (this.stateNote) this.stateNote.textContent = note;
    // An empty sentence is not a bordered strip with nothing in it. Photo mode
    // ships with no note at all, so the whole notice goes with it.
    if (this.noticeEl) this.noticeEl.hidden = !note;
    // A "Your preview" slide left in the product gallery would show a photo the
    // order is not going to carry, so blank takes it out and photo puts it back
    // if there is still a crop to show.
    if (this.galleryEnabled) {
      if (blank) this.removeGallery();
      else if (this.anyCrop()) this.syncGallery();
    }
    if (blank) this.closeSheet();
    // One direction only. `applyMode` marks the chip and never selects it, so a
    // chip click that calls setMode does not come back round through here and
    // call setMode again.
    if (this.photoGroup) {
      this.markChoice(this.photoGroup, blank ? this.photoWithout : this.photoWith);
    }
    this.syncOfferLine(blank);
    this.syncVariant();
  }

  /**
   * The sentence under the price. "Add your photo for $10" is an invitation
   * while no photo is chosen and a contradiction once one is, because by then
   * the ten dollars is already inside the price the buyer is reading. Both
   * sentences are rendered by the offer lines block and exactly one is shown.
   *
   * Scoped to this block's own section: the recommendations further down the
   * page carry offer lines of their own, about other products.
   */
  syncOfferLine(blank) {
    const scope = this.closest('.shopify-section, [id^="shopify-section"]') || document;
    const want = blank ? 'off' : 'on';
    scope.querySelectorAll('[data-photo-line]').forEach((line) => {
      line.hidden = line.dataset.photoLine !== want;
    });
  }

  /**
   * The back photo's three properties ride the locket line, and only once the
   * buyer has actually placed a back photo. They are disabled the rest of the
   * time so a one-photo locket's line is unchanged, and in blank mode so a
   * blank locket carries nothing at all. With an add-on product configured
   * these inputs are not rendered: that photo rides the add-on line instead.
   */
  applySecondInputs() {
    const on = !this.blankMode && !!this.photos[2].set && !this.addonVariant;
    if (this.fileInput2) this.fileInput2.disabled = !on;
    if (this.cropInput2) this.cropInput2.disabled = !on;
    if (this.countInput) this.countInput.disabled = !on;
    // The source markers follow the carriers, blank mode included.
    this.applySourceInputs();
  }

  /* ---- the phone sheet ---------------------------------------------------
     One flow, two homes. Under 768px the whole flow lives inside a native
     <dialog>, so the focus trap, the Escape key and the top layer are the
     browser's; from 768px up it sits inline on the page. The markup is moved,
     never duplicated, so there is one state machine and one set of refs. */

  setupSheet() {
    if (!this.sheet || !this.flow) return;
    this.phone = window.matchMedia('(max-width: 767px)');
    // A browser with no modal dialog keeps the inline layout at every width.
    this.sheetSupported = typeof this.sheet.showModal === 'function';
    if (!this.sheetSupported) this.classList.add('mab-locket--no-sheet');
    this.syncLayout();

    // A rotation or a resize across the breakpoint must not strand the flow
    // inside a sheet nobody can open. Held on the instance so disconnectedCallback
    // can take it off again.
    this._onBreakpoint = () => {
      if (!this.phone.matches && this.sheet.open) this.closeSheet();
      this.syncLayout();
    };
    if (this.phone.addEventListener) this.phone.addEventListener('change', this._onBreakpoint);
    else this.phone.addListener(this._onBreakpoint);

    // One control, one screen. With no photo yet the button on the page opens
    // the picker itself: it used to open a sheet that held a second button,
    // which is two taps and two controls for one decision. Once a photo is set
    // it is the way back into the sheet, where Adjust, Remove and the rights
    // box live; on a phone the rights box is in the sheet's bar and there is no
    // other way to reach it.
    if (this.sheetTrigger) {
      this.sheetTrigger.addEventListener('click', () => {
        if (this.sheetTrigger.dataset.set === 'true') {
          this.openSheet();
          return;
        }
        this.editing = 1;
        this.fileInput.click();
      });
    }
    if (this.sheetClose) this.sheetClose.addEventListener('click', () => this.closeSheet());
    if (this.barAction) this.barAction.addEventListener('click', () => this.onBarAction());
    // Fires for the close button, for Escape and for close() alike.
    this.sheet.addEventListener('close', () => this.onSheetClose());
    this.syncSheet();
  }

  syncLayout() {
    if (this.sheetSupported === false) return;
    const phone = !!(this.phone && this.phone.matches);
    const target = phone ? this.sheetBody : this.inlineHome;
    const moving = !!(target && this.flow && this.flow.parentElement !== target);
    if (moving) target.appendChild(this.flow);

    // The rights box gates the bar's action, so in the sheet it lives in the
    // bar with it. The sheet body scrolls: at 390px on step 3 the box sat below
    // the fold while the bar read "Tick the rights box to continue", so the one
    // fully visible control on the screen named a control that was not on it.
    // Moved after the flow, because the flow move takes its home with it.
    // Safe because the checkbox carries form=, so its parent has nothing to do
    // with which form it submits with.
    const rightsTarget = phone ? this.barRights : this.rightsHome;
    if (this.rightsRow && rightsTarget && this.rightsRow.parentElement !== rightsTarget) {
      rightsTarget.appendChild(this.rightsRow);
    }

    // The viewport is 280px inline and full width in the sheet, so everything
    // drawn to a canvas has to be measured and repainted after the move.
    if (moving) this.onResize();
  }

  openSheet() {
    if (!this.sheet) return;
    if (typeof this.sheet.showModal !== 'function') {
      // No modal dialog on this browser. Put the flow back on the page and let
      // the inline layout do the work, rather than throwing on a tap.
      this.classList.add('mab-locket--no-sheet');
      if (this.inlineHome && this.flow) this.inlineHome.appendChild(this.flow);
      this.onResize();
      return;
    }
    this.syncLayout();
    // showModal blocks the page for a pointer, but iOS Safari still scrolls the
    // document behind a modal, so the theme's own lock goes on too.
    document.documentElement.setAttribute('scroll-lock', '');
    this.sheet.showModal();
    this.syncSheet();
    requestAnimationFrame(() => this.onResize());
  }

  /**
   * Open the sheet only where the sheet is the layout: under 768px, with a
   * browser that has a modal dialog, and only when it is not open already.
   * Above that width the flow is inline on the page and there is nothing to
   * open.
   */
  openSheetOnPhone() {
    if (!this.sheet || this.sheet.open) return;
    if (this.sheetSupported === false) return;
    if (!(this.phone && this.phone.matches)) return;
    this.openSheet();
  }

  closeSheet() {
    if (this.sheet && this.sheet.open) this.sheet.close();
  }

  onSheetClose() {
    document.documentElement.removeAttribute('scroll-lock');
    // Most browsers return focus to the opener themselves. Saying it here means
    // the ones that do not still land the buyer back where they were.
    //
    // preventScroll, because the sheet also closes on the way into the cart:
    // submitFromSheet closes it and then clicks the page's own add-to-cart
    // button, and a plain focus() would scroll the product page to the trigger
    // under the cart drawer that is opening over it.
    if (this.sheetTrigger) this.sheetTrigger.focus({ preventScroll: true });
  }

  /**
   * The steps are the stage's three states. Everything that names a step reads
   * from here, so the sheet title, the bar and the list can never disagree.
   *
   * TODO(maintainer): the back photo repeats these same three steps and these
   * same three labels, so a buyer placing the second face reads "Choose your
   * photo" again with nothing saying which face she is on. No locket has a
   * two-window aperture in the store yet, so this has never been seen by a
   * customer. When one does, give the labels a per-face variant (the face names
   * are already block settings, side_one_label and side_two_label).
   */
  syncSheet() {
    const step = this.step;
    if (this.stepsList) {
      this.stepsList.querySelectorAll('.mab-locket__step').forEach((item) => {
        if (parseInt(item.dataset.step, 10) === step) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
    }
    const labels = {
      1: this.dataset.stepOneLabel,
      2: this.dataset.stepTwoLabel,
      3: this.dataset.stepThreeLabel,
    };
    if (this.sheetTitle) this.sheetTitle.textContent = labels[step] || '';
    if (this.barAction) {
      // One primary action per step, and step 1 has none: its one button is in
      // the body of the sheet, and a bar action under it was the second control
      // the owner rejected. An empty label takes the button out of the bar
      // rather than leaving a blank one there.
      const ready = this.isReady();
      const actions = {
        1: '',
        2: this.dataset.applyLabel,
        3: this.dataset.atcLabel,
      };
      this.barAction.textContent = actions[step] || '';
      this.barAction.hidden = !actions[step];
      // Genuinely disabled, not marked. The old design let the buyer tap a
      // button that was not going to work and then explained, which is a second
      // confirm step wearing the clothes of the first. The button says "Add to
      // cart" at every moment of step 3; until the rights box is ticked it is
      // unavailable, the box beside it is highlighted, and that is the
      // explanation. A screen reader gets the same reason through
      // aria-describedby pointing at the checkbox's own row, which is inside
      // the dialog and so is not inert the way the helper line outside it is.
      const gated = step === 3 && !ready;
      this.barAction.disabled = gated || !!this._busy;
      this.barAction.removeAttribute('aria-disabled');
      const rightsId = this.rightsRow && this.rightsRow.id;
      if (gated && rightsId) this.barAction.setAttribute('aria-describedby', rightsId);
      else this.barAction.removeAttribute('aria-describedby');
    }
    // One attribute, not a written string. The button on the page holds both of
    // the things it can say in its markup and CSS picks; writing text here
    // would wipe whichever line the stylesheet had chosen.
    if (this.sheetTrigger) this.sheetTrigger.dataset.set = step === 3 ? 'true' : 'false';
    if (this.stepStatus) {
      const pattern = this.dataset.stepStatus || '';
      const spoken = pattern.replace('[n]', String(step)).replace('[label]', labels[step] || '');
      if (this.stepStatus.textContent !== spoken) this.stepStatus.textContent = spoken;
    }
  }

  /**
   * What the gated wording should say, which depends on what is missing. Once
   * the photo is placed, "Add your photo" sends the buyer looking for something
   * she has already done; the thing left is the rights box.
   */
  gatedLabel() {
    const hasPhoto = this.fileInput.files.length > 0 && !!this.cropInput.value;
    if (hasPhoto && this.rights && !this.rights.checked) {
      return this.dataset.stickyRightsLabel || this.dataset.stickyLabel || '';
    }
    return this.dataset.stickyLabel || '';
  }

  onBarAction() {
    // Step 1 has no bar action at all, so there is nothing to answer for it.
    if (this.step === 1) {
      return;
    } else if (this.step === 2) {
      this.applyCrop();
    } else {
      this.submitFromSheet();
    }
  }

  /**
   * One tap adds to the cart and opens the drawer, and the buyer never returns
   * to the product page to press anything a second time.
   *
   * The drawer opens on the tap, not on the answer. Measured on a phone over a
   * slow connection, the add took 2.6 seconds and nothing moved on screen for
   * 4.8, which reads as a dead button. The locket's picture, name and price are
   * all known here before anything is sent, so the drawer can show the line
   * while the upload is still going and swap it for the real one when the cart
   * answers. What it must never do is show it as added when the add failed, so
   * the pending line comes out on both endings and the failure re-opens the
   * sheet with the message.
   *
   * The sheet never owns availability: it submits the theme's own form, so sold
   * out, a variant change or a cart error is answered by the theme and not by a
   * second implementation here. The sheet has to close first, because
   * everything outside a modal dialog is inert.
   *
   * `requestSubmit` rather than clicking the button: the click path also runs
   * the fly-to-cart animation and the button's own "Added" animation, which are
   * main-thread work in the frame where the drawer is trying to paint, and a
   * picture flying to a cart icon says nothing the open drawer is not already
   * saying. It still runs the form's own validity check first, so nothing is
   * skipped. The fallback is the old click for a browser without it.
   *
   * The two frames between the open and the submit are the point: reading the
   * File out of the form and building the multipart body is a long task, and in
   * the same tick as the open the buyer waits for it before she sees anything.
   */
  submitFromSheet() {
    if (!this.isReady()) {
      this.nudgeRights();
      return;
    }
    const button = this.form && this.form.querySelector('[ref="addToCartButton"]');
    if (!button) return;
    // A disabled button is the one submit the theme drops without saying
    // anything, so it is turned away here rather than leaving a pending line in
    // the drawer with nothing coming to take it out. Sold out behaves exactly as
    // it did before this slice, which is to do nothing.
    if (button.disabled) return;
    this._pendingAdd = true;
    this._sheetAdd = true;
    this.closeSheet();
    this.showPendingLine();
    this.openCartDrawer();
    const send = () => {
      if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
      else button.click();
    };
    requestAnimationFrame(() => requestAnimationFrame(send));
  }

  openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  closeCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.close === 'function') drawer.close();
  }

  /* ---- the pending line -------------------------------------------------- */

  /**
   * The locket, in the drawer, before the cart has heard of it.
   *
   * Everything on it is known client-side: the picture and the name come off
   * the product, the price off the variant that is selected right now, and the
   * photo line off how many photos she placed. It is marked aria-busy and it
   * carries no quantity control and no remove button, because there is nothing
   * there yet to change or to remove.
   *
   * It goes in the drawer's own items container, first, which is where Shopify
   * puts the newest line, so the real line arrives in the same place and the
   * swap is not a jump.
   *
   * On a cart that was empty the drawer's markup is the theme's empty state, and
   * for a while this line was being grafted into the middle of it: no header, no
   * meter, no Check out, and the locket she had just bought sitting above "Have
   * an account? Log in to check out faster." and a Continue shopping button, for
   * the whole second and a half before the cart answered. The empty branch now
   * also ships `snippets/mab-pending-cart.liquid`, the real furniture, hidden;
   * this method fills it in and `.cart-drawer--pending` swaps the two. Where
   * that scaffold exists it is where the line goes, and where it does not, which
   * is every cart that already had something in it, nothing about this changes.
   */
  showPendingLine() {
    const dialog = document.querySelector('cart-drawer-component .cart-drawer__dialog');
    // The scaffold's container first: on an empty cart it is the one that is
    // about to be on screen, and the empty state's own is about to be hidden.
    const items =
      dialog && (dialog.querySelector('[data-pending-items]') || dialog.querySelector('.cart-drawer__items'));
    if (!items) return;
    this.removePendingLine();
    this.fillPendingCart(dialog);

    const row = document.createElement('div');
    row.className = 'mab-pending';
    row.setAttribute('data-mab-pending', 'true');
    row.setAttribute('aria-busy', 'true');

    const src = this.dataset.pendingImage || '';
    if (src) {
      const img = document.createElement('img');
      img.className = 'mab-pending__image';
      img.src = src;
      img.alt = '';
      img.width = 56;
      img.height = 56;
      row.appendChild(img);
    }

    const info = document.createElement('div');
    info.className = 'mab-pending__info';

    const title = document.createElement('p');
    title.className = 'mab-pending__title';
    title.textContent = this.dataset.pendingTitle || '';
    info.appendChild(title);

    const note = this.photoNote();
    if (note) {
      const noteEl = document.createElement('p');
      noteEl.className = 'mab-pending__note';
      noteEl.textContent = note;
      info.appendChild(noteEl);
    }

    const status = document.createElement('p');
    status.className = 'mab-pending__status';
    status.textContent = this.dataset.pendingStatus || '';
    info.appendChild(status);

    row.appendChild(info);

    const price = this.pendingPrice();
    if (price) {
      const priceEl = document.createElement('p');
      priceEl.className = 'mab-pending__price';
      priceEl.textContent = price;
      row.appendChild(priceEl);
    }

    items.prepend(row);
    dialog.classList.add('cart-drawer--pending');
  }

  /**
   * The numbers on the stand-in cart, for the cart that is about to exist.
   *
   * Only ever one line, because the scaffold only exists in the drawer's empty
   * branch, so the meter is this line's price against the threshold and the
   * total is that price. Both come pre-formatted from Liquid, per variant, so no
   * money is formatted here.
   *
   * Both sentences are already in the scaffold as markup. This chooses one and
   * writes an amount and a width into it. Nothing here builds a sentence: `t`
   * HTML-escapes what it returns, so a sentence carried through a data attribute
   * and set with textContent would print "You&#39;re" at the buyer.
   *
   * Everything is optional. A shop with no free shipping renders no meter
   * in the scaffold at all, and this walks past it.
   *
   * @param {Element} dialog
   */
  fillPendingCart(dialog) {
    const heading = dialog.querySelector('[data-pending-heading]');
    if (heading) heading.hidden = false;

    const total = dialog.querySelector('[data-pending-total]');
    if (total) total.textContent = this.pendingPrice();

    const meters = safeJSON(this.dataset.pendingMeter) || {};
    const input = this.form && this.form.querySelector('input[name="id"]');
    const id = (input && input.value) || this.dataset.pendingVariant;
    const meter = meters[id] || meters[this.dataset.pendingVariant];
    if (!meter) return;

    const away = dialog.querySelector('[data-pending-meter-text]');
    const reached = dialog.querySelector('[data-pending-meter-reached]');
    const amount = dialog.querySelector('[data-pending-meter-amount]');
    const fill = dialog.querySelector('[data-pending-meter-fill]');

    const isReached = Number(meter.r) === 1;
    if (away) away.hidden = isReached;
    if (reached) reached.hidden = !isReached;
    if (amount && !isReached) amount.textContent = meter.b || '';
    if (fill) fill.style.width = `${Math.max(0, Math.min(100, Number(meter.f) || 0))}%`;
  }

  /**
   * Deliberately no timeout on this line.
   *
   * A clock here would be a way to charge somebody twice. Shopify answers a
   * timed-out upload at the gateway after tens of seconds, and the theme then
   * waits, asks the cart whether the line landed, and sends the whole photo
   * again; that sequence can run past a minute and still succeed. A timer that
   * gave up in the middle of it would put "that did not work" and a live button
   * in front of a buyer whose add was still in flight, and her next tap would be
   * a second locket. The double-charge guard in product-form.js cannot catch
   * that, because from its side the second tap is a new add and not a retry.
   *
   * Every submit that reaches the network ends in a cart event, success or
   * failure, and both endings take this line out. The paths that never reach
   * the network are refused before the line is drawn: a disabled button is
   * turned away in submitFromSheet, and a form the browser refuses to submit
   * clears the line from the invalid handler, which knows nothing was sent.
   */
  removePendingLine() {
    document.querySelectorAll('[data-mab-pending]').forEach((el) => el.remove());
    const dialog = document.querySelector('cart-drawer-component .cart-drawer__dialog');
    if (!dialog) return;
    dialog.classList.remove('cart-drawer--pending');
    const heading = dialog.querySelector('[data-pending-heading]');
    if (heading) heading.hidden = true;
  }

  /** "Your photo is inside", or the plural when the back face is set too. */
  photoNote() {
    if (this.blankMode) return '';
    const two = this.photos[2] && this.photos[2].set;
    return (two ? this.dataset.photoNoteTwo : this.dataset.photoNoteOne) || '';
  }

  /**
   * What this locket costs, for the variant selected at the moment of the tap.
   * Liquid wrote every variant's price out, so this is the store's own money
   * format and not a number formatted a second way in JavaScript.
   */
  pendingPrice() {
    const prices = safeJSON(this.dataset.pendingPrices) || {};
    const input = this.form && this.form.querySelector('input[name="id"]');
    const id = input && input.value;
    return (id && prices[id]) || prices[this.dataset.pendingVariant] || '';
  }

  /**
   * The add did not land, so the page must not behave as though it had.
   *
   * Shopify's upload path times out on a real photo often enough to see it in
   * one evening of testing. The theme retries once and then reports it; this
   * puts the buyer back on step 3 with the message where she is already
   * looking, instead of on a product page beside an empty cart.
   *
   * One announcer, not three. The error line is role="alert", which already
   * speaks, and the theme's own inline error carries the same sentence because
   * bindAddToCart handed it over. Writing into the form's live region as well
   * made a screen reader say the failure twice, in two different wordings.
   */
  handleAddFailure() {
    if (!this._pendingAdd || this.blankMode) return;
    this._pendingAdd = false;
    this.setState('ready');
    if (this.phone && this.phone.matches) this.openSheet();
    this.showError(this.dataset.msgAddFailed || '');
    if (this.barAction) this.barAction.focus();
  }

  // Replace, Remove, Edit and the second-photo offer each appear in more than
  // one state; `slot` says which face they act on.
  act(name, slot) {
    if (name === 'add-second') {
      this.editing = 2;
      this.hideError();
      (this.picker2 || this.fileInput).click();
    } else if (name === 'remove') {
      if (slot === 2) this.removeSecond();
      // Removing the front face resets the whole thing. The button that was
      // pressed goes with it, so focus would land on the body and the Undo row
      // would arrive with nobody looking at it.
      else this.reset({ announce: true, focusUndo: true });
    } else if (name === 'replace') {
      if (slot === 2) {
        this.editing = 2;
        this.clearSlot(2);
        (this.picker2 || this.fileInput).click();
      } else {
        // She asked to swap it, not to lose it, so the row says cleared rather
        // than removed and stays quiet: the file picker is about to open.
        this.reset({ note: this.dataset.undoNoteReplace, keepChoice: true });
        this.fileInput.click();
      }
    } else if (name === 'edit') {
      this.editing = slot;
      // Back into this slot's own window shape. The two faces of one locket do
      // not photograph as the same shape, so the crop box changes with the slot.
      this.applyCropAspect(slot);
      this.setState('cropping');
      requestAnimationFrame(() => {
        if (this.cur().bitmap) this.clamp();
        this.render();
      });
    } else if (name === 'reset') {
      // Back to the framing the cropper opened with. Not a removed photo: the
      // buyer asked to start placing again, not to start over.
      if (this.cur().bitmap) this.setupImage();
    } else if (name === 'undo') {
      this.restoreRemoved();
    }
  }

  /* ---- aperture windows + base image (Phase 1.5 / 1.7) ------------------- */

  parseAperture() {
    // Effective aperture per window, layered: built-in floor, then the
    // block-setting default (position), then the shadow ranges, then the
    // per-product metafield JSON. A value may be a single window (object) or
    // two windows (array); two windows turns on the second-photo offer.
    const floor = { cx: 0.3, cy: 0.5, w: 0.25, h: 0.49, rot: 0, shadow: 45, feather: 14 };
    const shadow = pickAperture({
      shadow: this.dataset.shadowStrength,
      feather: this.dataset.shadowFeather,
    });
    const def = asWindows(safeJSON(this.dataset.apertureDefault));
    const mf = asWindows(safeJSON(this.dataset.aperture));
    // The metafield decides the window count when present; otherwise the block
    // default does.
    const src = mf.length ? mf : def;
    const count = Math.max(1, src.length);
    this.windows = [];
    for (let i = 0; i < count; i += 1) {
      const d = pickAperture(def[i] || def[0] || {});
      const m = pickAperture(mf[i] || {});
      this.windows.push(Object.assign({}, floor, d, shadow, m));
    }
    this.hasSecondWindow = this.windows.length >= 2;
    // Only a per-product aperture describes a real recess on a real photo. The
    // block default is a demo position fitted to the bundled placeholder, so a
    // product without the metafield keeps the block's own crop-box ratio rather
    // than taking its shape from somebody else's locket.
    this.hasAperture = mf.length > 0;
  }

  // Ask for the base photo once, the first time something needs to draw it.
  ensureBase() {
    if (this._baseRequested) return this.baseReady;
    this._baseRequested = true;
    this.loadBase();
    return this.baseReady;
  }

  loadBase() {
    const url = this.dataset.baseImage;
    this.baseIsPlaceholder = this.dataset.basePlaceholder === 'true';
    this.baseImg = null;
    if (!url) {
      this.baseReady = Promise.resolve(false);
      return;
    }
    const img = new Image();
    img.decoding = 'async';
    // Display-only: the composite canvas is never read back or uploaded, so a
    // cross-origin base image (Shopify CDN) tainting the canvas does not matter.
    this.baseReady = new Promise((res) => {
      img.onload = () => {
        this.baseImg = img;
        this.applyBaseAspect();
        res(true);
      };
      img.onerror = () => {
        this.baseImg = null;
        res(false);
      };
    });
    img.src = url;
  }

  applyBaseAspect() {
    if (!this.baseImg) return;
    const bw = this.baseImg.naturalWidth;
    const bh = this.baseImg.naturalHeight;
    if (!bw || !bh) return;
    // Liquid could not measure this source, so the image itself is the answer.
    // Late, but still before anything is drawn into a window.
    if (!(this.baseDims.w > 0) || !(this.baseDims.h > 0)) {
      this.baseDims = { w: bw, h: bh };
      this.applyCropAspect();
    }
    if (this.compositeStage) this.compositeStage.style.aspectRatio = `${bw} / ${bh}`;
  }

  /**
   * The shape of one window, as it renders. The aperture is normalised against
   * the base photo, so a window that is 0.112 of the width and 0.13 of the
   * height of a 768 x 767 photo comes out 0.862 wide for every 1 tall. The crop
   * box in step 2 has to be that shape, because paintComposite stretches the
   * crop rect to fill the window: 1.0 into 0.862 is a face squeezed sideways by
   * 14 percent, which is what the 16 September phone test found.
   *
   * This is a screen ratio and nothing else. The aperture was fitted by eye
   * over a photograph, so it carries the camera angle as well as the recess,
   * which is right for a preview and wrong for a cut. A printed size comes from
   * Locket Print Studio's own shape registry in millimetres
   * (07-software/locket-print-studio/src/shapes/registry.ts, widthMm and
   * heightMm), or from a measurement the owner types into that tool. It never
   * comes from here, and it never comes from a Shopify metafield: the tool
   * reads no Shopify data at all.
   */
  windowAspect(slot) {
    const fallback = this.aspectW / this.aspectH;
    if (!this.hasAperture) return fallback;
    const a = this.windows[(slot || 1) - 1] || this.windows[0];
    const bw = this.baseDims.w;
    const bh = this.baseDims.h;
    if (!a || !(a.w > 0) || !(a.h > 0) || !(bw > 0) || !(bh > 0)) return fallback;
    const ratio = (a.w * bw) / (a.h * bh);
    return ratio > 0 && Number.isFinite(ratio) ? ratio : fallback;
  }

  /**
   * Point the crop box at the window the buyer is filling. One custom property
   * drives the step 2 viewport and the bezel fallback preview, so those two and
   * the chip below can never disagree with each other or with the composite.
   */
  applyCropAspect(slot) {
    const ratio = this.windowAspect(slot || this.editing || 1);
    this.style.setProperty('--mab-locket-aspect', String(ratio));
    return ratio;
  }

  /** True when the window shape is still waiting on the base photo to load. */
  needsBaseDims() {
    if (!this.hasAperture || !this.dataset.baseImage) return false;
    return !(this.baseDims.w > 0) || !(this.baseDims.h > 0);
  }

  // A Path2D for a window shape in this locket's shape. The outline itself is
  // the shared one in mab-photo-core.js.
  shapePath(cx, cy, aw, ah, rotDeg) {
    return shapePath(this.shape, cx, cy, aw, ah, rotDeg);
  }

  // The block's in-panel composite preview (stage box, display resolution).
  drawComposite() {
    if (!this.baseImg || !this.compositeStage) return;
    const cssW = this.compositeStage.clientWidth;
    const cssH = this.compositeStage.clientHeight;
    if (!cssW || !cssH) return;
    const dpr = window.devicePixelRatio || 1;
    this.paintComposite(this.compositeCanvas, cssW, cssH, dpr, null);
  }

  /**
   * Paint the in-locket composite into any canvas: the open-locket base image
   * (contain-fit), then for EACH window the matching face's cropped photo
   * (masked to the shape, rotated) with a soft inner shadow. Shared by the
   * block preview, the gallery slide, and the high-res lightbox render.
   */
  paintComposite(canvas, cssW, cssH, scale, bg) {
    if (!this.baseImg || !cssW || !cssH) return false;
    canvas.width = Math.round(cssW * scale);
    canvas.height = Math.round(cssH * scale);
    const ctx = canvas.getContext('2d');
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);
    if (bg) {
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, cssW, cssH);
    }

    const bw = this.baseImg.naturalWidth || this.baseImg.width;
    const bh = this.baseImg.naturalHeight || this.baseImg.height;
    const k = Math.min(cssW / bw, cssH / bh);
    const Bw = bw * k;
    const Bh = bh * k;
    const ox = (cssW - Bw) / 2;
    const oy = (cssH - Bh) / 2;
    ctx.drawImage(this.baseImg, ox, oy, Bw, Bh);

    this.windows.forEach((a, i) => {
      const Cx = ox + a.cx * Bw;
      const Cy = oy + a.cy * Bh;
      const Aw = a.w * Bw;
      const Ah = a.h * Bh;
      if (Aw <= 0 || Ah <= 0) return;
      const shape = this.shapePath(Cx, Cy, Aw, Ah, a.rot);
      const slot = this.photos[i + 1];

      if (slot && slot.bitmap && slot.crop) {
        const { x, y, w, h } = slot.crop;
        ctx.save();
        ctx.clip(shape);
        ctx.translate(Cx, Cy);
        ctx.rotate((a.rot * Math.PI) / 180);
        ctx.drawImage(slot.bitmap, x, y, w, h, -Aw / 2, -Ah / 2, Aw, Ah);
        ctx.restore();
      } else if (this.tuningEnabled) {
        ctx.save();
        ctx.clip(shape);
        ctx.fillStyle = 'rgba(176, 141, 87, 0.28)';
        ctx.fillRect(Cx - Aw, Cy - Ah, Aw * 2, Ah * 2);
        ctx.restore();
      }

      const blur = Math.max(0, (a.feather / 100) * Math.min(Aw, Ah));
      const alpha = Math.max(0, Math.min(0.9, a.shadow / 100));
      this.drawInnerShadow(ctx, shape, Cx, Cy, Math.max(Aw, Ah), `rgba(0,0,0,${alpha})`, blur);
    });
    return true;
  }

  /* ---- product media gallery bridge (Phase 1.6) -------------------------- */

  initGallery() {
    this.previewSlideId = 'mab-your-preview';
    this.galleryLabel = this.dataset.galleryLabel || 'Your preview';
    this._galleryEntry = null;
    this._onVariantUpdate = () => {
      this._galleryEntry = null;
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (this.anyCrop()) this.syncGallery();
        })
      );
    };
    document.addEventListener('variant:update', this._onVariantUpdate);
  }

  get galleryEl() {
    return document.querySelector('media-gallery');
  }

  anyCrop() {
    return this.photos[1].set || this.photos[2].set;
  }

  syncGallery() {
    if (!this.galleryEnabled) return;
    const gallery = this.galleryEl;
    if (!gallery) {
      this.classList.remove('mab-locket--in-gallery');
      return;
    }
    if (!this.anyCrop() || !this.baseImg) {
      this.removeGallery();
      return;
    }
    let e = this._galleryEntry;
    let isNew = false;
    if (!e || !e.anchor || !e.anchor.isConnected || e.gallery !== gallery) {
      e = this.buildGallery(gallery);
      if (!e) {
        this.classList.remove('mab-locket--in-gallery');
        return;
      }
      this._galleryEntry = e;
      isNew = true;
    }
    this.classList.add('mab-locket--in-gallery');
    requestAnimationFrame(() => {
      this.paintGallery(e);
      if (isNew) requestAnimationFrame(() => this.featureGallery(e));
    });
  }

  buildGallery(gallery) {
    const scroller = gallery.querySelector('slideshow-slides');
    const gridUl = gallery.querySelector('.media-gallery__grid');
    const zoomUl = gallery.querySelector('.dialog-zoomed-gallery');
    const zoomThumbs = gallery.querySelector('.dialog-thumbnails-list');
    const zoomDialog = gallery.querySelector('zoom-dialog');
    const slideshow = gallery.querySelector('slideshow-component');
    if (!scroller && !gridUl) return null;

    const index = scroller
      ? scroller.querySelectorAll(':scope > slideshow-slide').length
      : gridUl.querySelectorAll(':scope > li').length;
    const zoomId = zoomDialog && zoomDialog.id ? zoomDialog.id : '';
    const ratio = this.baseImg ? (this.baseImg.naturalWidth || 1) / (this.baseImg.naturalHeight || 1) : 1;
    const label = this.galleryLabel;

    const badge = () => `<span class="mab-preview-badge">${escapeHtml(label)}</span>`;
    const mediaHtml = (canvasClass, mediaRatio = ratio) =>
      `<div class="product-media mab-preview-media" style="--ratio:${mediaRatio};"><canvas class="product-media__image ${canvasClass}"></canvas></div>`;

    // The gallery settings decide how a slide is framed, and the Liquid writes
    // that into each native slide: constrain-height and a media-fit class when
    // the photo is held to the window, and a constraint wrapper round the media
    // only when a fixed ratio is set (with Auto there is none). Copy all of it
    // from the first native slide rather than restating it here, so the preview
    // is framed exactly like the photo beside it whatever the owner picks in the
    // theme editor. The frame takes the first photo's ratio, so the preview's
    // box matches the first slide's; the composite is fitted inside it.
    const nativeSlide = scroller?.querySelector(':scope > slideshow-slide.product-media-container:not(.mab-preview-container)');
    const nativeLi = gridUl?.querySelector(':scope > li.product-media-container:not(.mab-preview-container)');
    const firstMedia = (nativeSlide || nativeLi)?.querySelector('.product-media');
    const frameRatio = parseRatio(firstMedia?.style.getPropertyValue('--ratio')) || ratio;
    const nativeThumb = zoomThumbs?.querySelector('.dialog-thumbnails-list__thumbnail:not(.mab-preview-thumb)');
    const thumbRatio = parseRatio(nativeThumb?.style.getPropertyValue('--aspect-ratio')) || frameRatio;
    const framing = (native) =>
      native ? [...native.classList].filter((c) => c === 'constrain-height' || c.startsWith('media-fit-')) : [];
    const framedMediaHtml = (native, canvasClass) => {
      const inner = mediaHtml(canvasClass, frameRatio);
      return native?.querySelector(':scope > .product-media-constraint-wrapper')
        ? `<div class="product-media-constraint-wrapper">${inner}</div>`
        : inner;
    };

    const entry = { gallery, zoomDialog, slide: null, gridLi: null, zoomLi: null, zoomThumb: null, anchor: null, counters: [], thumbRatio };

    // 1) Carousel slide, at every width.
    if (scroller) {
      const slide = document.createElement('slideshow-slide');
      slide.setAttribute('ref', 'slides[]');
      slide.setAttribute('slide-id', this.previewSlideId);
      slide.setAttribute('aria-hidden', 'false');
      slide.className =
        'product-media-container product-media-container--image product-media-container--zoomable mab-preview-container';
      slide.classList.add(...framing(nativeSlide));
      // Liquid means to set this on every slide under Auto, and the snippet
      // loses it to a second style attribute. Set here it gives the slide a
      // definite height for the canvas to fill, held by the same max-height.
      slide.style.setProperty('--media-preview-ratio', String(frameRatio));
      const fit = nativeSlide?.style.getPropertyValue('--product-media-fit');
      if (fit) slide.style.setProperty('--product-media-fit', fit.trim());
      if (zoomId) slide.setAttribute('on:click', `#${zoomId}/open/${index}`);
      slide.innerHTML = badge() + framedMediaHtml(nativeSlide, 'mab-preview-canvas');
      scroller.appendChild(slide);
      entry.slide = slide;
      entry.slideCanvas = slide.querySelector('.mab-preview-canvas');
    }

    // 2) Desktop grid item.
    if (gridUl) {
      const li = document.createElement('li');
      li.setAttribute('ref', 'media[]');
      li.className =
        'product-media-container product-media-container--image product-media-container--zoomable mab-preview-container';
      li.classList.add(...framing(nativeLi));
      li.style.setProperty('--media-preview-ratio', String(frameRatio));
      const zoomBtn = zoomId
        ? `<button type="button" class="button button-unstyled product-media-container__zoom-button" aria-label="${escapeHtml(
            label
          )}" on:click="/zoom/${index}"><span class="visually-hidden">${escapeHtml(label)}</span></button>`
        : '';
      li.innerHTML = badge() + zoomBtn + framedMediaHtml(nativeLi, 'mab-preview-canvas-grid');
      gridUl.appendChild(li);
      entry.gridLi = li;
      entry.gridCanvas = li.querySelector('.mab-preview-canvas-grid');
    }

    // 3) Lightbox media + thumbnail (high-res).
    if (zoomUl) {
      const li = document.createElement('li');
      li.setAttribute('ref', 'media[]');
      li.className = 'product-media-container product-media-container--image product-media-container--zoomable mab-preview-container mab-preview-zoom';
      li.setAttribute('on:click', '/close');
      li.innerHTML = `<drag-zoom-wrapper class="product-media__drag-zoom-wrapper">${mediaHtml(
        'mab-preview-canvas-zoom'
      )}</drag-zoom-wrapper>`;
      zoomUl.appendChild(li);
      entry.zoomLi = li;
      entry.zoomCanvas = li.querySelector('.mab-preview-canvas-zoom');
    }
    if (zoomThumbs) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'button button-unstyled dialog-thumbnails-list__thumbnail mab-preview-thumb';
      btn.setAttribute('aria-label', label);
      // The same two properties the Liquid writes on its own thumbnails.
      btn.style.setProperty('--aspect-ratio', String(thumbRatio));
      btn.style.setProperty('--gallery-aspect-ratio', String(thumbRatio));
      if (zoomDialog) {
        btn.setAttribute('on:click', `#${zoomId}/handleThumbnailClick/${index}`);
      }
      btn.innerHTML = `<canvas class="mab-preview-canvas-thumb"></canvas>`;
      zoomThumbs.appendChild(btn);
      entry.zoomThumb = btn;
      entry.thumbCanvas = btn.querySelector('.mab-preview-canvas-thumb');
    }

    // 4) Bump the counter total(s) so "N / total" stays honest.
    gallery.querySelectorAll('.slideshow-controls__counter').forEach((counter) => {
      const totalNode = counter.lastChild;
      if (totalNode && totalNode.nodeType === 3) {
        if (counter._mabOrigTotal == null) counter._mabOrigTotal = totalNode.textContent;
        totalNode.textContent = String(index + 1);
        entry.counters.push(counter);
      }
    });

    entry.anchor = entry.slide || entry.gridLi;
    entry.index = index;

    gallery.updatedCallback?.();
    zoomDialog?.updatedCallback?.();
    slideshow?.updatedCallback?.();
    return entry;
  }

  paintGallery(e) {
    const dpr = window.devicePixelRatio || 1;
    const bg = this._galleryBg || (this._galleryBg = this.galleryBgColor(e.gallery));
    if (e.slideCanvas) this.paintGalleryCanvas(e.slideCanvas, dpr, bg);
    if (e.gridCanvas) this.paintGalleryCanvas(e.gridCanvas, dpr, bg);
    if (e.thumbCanvas) this.paintComposite(e.thumbCanvas, 240, Math.round(240 / (e.thumbRatio || 1)), 1, bg);
    if (e.zoomCanvas) this.paintZoomCanvas(e.zoomCanvas);
  }

  paintGalleryCanvas(canvas, dpr, bg) {
    const cssW = canvas.clientWidth;
    const cssH = canvas.clientHeight;
    if (!cssW || !cssH) return;
    this.paintComposite(canvas, cssW, cssH, dpr, bg);
  }

  paintZoomCanvas(canvas) {
    const bw = this.baseImg.naturalWidth || this.baseImg.width;
    const bh = this.baseImg.naturalHeight || this.baseImg.height;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const targetW = Math.min(Math.max(bw, Math.round(bw * dpr)), 2400);
    const targetH = Math.round((targetW * bh) / bw);
    this.paintComposite(canvas, targetW, targetH, 1, null);
  }

  // Correct the injected slide's counter to read N/N. The theme's slideshow
  // derives the current index from an IntersectionObserver that only ever
  // tracked the native slides, so any scroll re-syncs `current` back to the
  // last native slide (the "3/4" bug). We set `current` explicitly after the
  // scroll settles, and keep it honest by re-asserting whenever a scroll ends
  // with our slide geometrically centred (which the theme cannot detect).
  featureGallery(e) {
    const slideshow = e.gallery.querySelector('slideshow-component');
    const scroller = e.gallery.querySelector('slideshow-slides');
    if (!slideshow || !e.slide) {
      e.gridLi?.scrollIntoView?.({ block: 'nearest' });
      return;
    }

    const indexOf = () => {
      const slides = slideshow.slides;
      if (!slides) return -1;
      return Array.prototype.indexOf.call(slides, e.slide);
    };

    const assert = () => {
      slideshow.updatedCallback?.();
      const idx = indexOf();
      if (idx < 0) return;
      try {
        slideshow.select({ id: this.previewSlideId }, undefined, { animate: false });
      } catch (_) {
        /* no-op */
      }
      // Re-assert the index directly, since select's own scroll can re-sync away.
      try {
        slideshow.current = idx;
      } catch (_) {
        /* no-op */
      }
    };

    assert();
    requestAnimationFrame(() => requestAnimationFrame(assert));
    setTimeout(assert, 250);

    // Persistent guard: whenever a scroll settles with our slide centred, the
    // theme's #sync reports the nearest OBSERVED (native) slide; correct it.
    if (scroller && !e._scrollBound) {
      e._scrollBound = true;
      let t;
      const onScroll = () => {
        clearTimeout(t);
        t = setTimeout(() => {
          if (!e.slide || !e.slide.isConnected) return;
          if (!this.slideCentred(scroller, e.slide)) return;
          const idx = indexOf();
          if (idx >= 0 && slideshow.current !== idx) {
            try {
              slideshow.current = idx;
            } catch (_) {
              /* no-op */
            }
          }
        }, 120);
      };
      scroller.addEventListener('scroll', onScroll, { passive: true });
      e._scrollHandler = onScroll;
      e._scroller = scroller;
    }

    e.gridLi?.scrollIntoView?.({ block: 'nearest' });
  }

  slideCentred(scroller, slide) {
    const sr = scroller.getBoundingClientRect();
    const er = slide.getBoundingClientRect();
    if (!er.width) return false;
    const slideCenter = er.left + er.width / 2;
    const ref = sr.left + sr.width / 2;
    return Math.abs(slideCenter - ref) < er.width / 2;
  }

  removeGallery() {
    const e = this._galleryEntry;
    this.classList.remove('mab-locket--in-gallery');
    if (!e) return;
    if (e._scroller && e._scrollHandler) e._scroller.removeEventListener('scroll', e._scrollHandler);
    const slideshow = e.gallery?.querySelector('slideshow-component');
    // Pull `current` back onto a real slide before the injected one is gone.
    if (slideshow && e.index != null) {
      try {
        if (slideshow.current >= e.index) slideshow.current = Math.max(0, e.index - 1);
      } catch (_) {
        /* no-op */
      }
    }
    e.slide?.remove();
    e.gridLi?.remove();
    e.zoomLi?.remove();
    e.zoomThumb?.remove();
    e.counters?.forEach((c) => {
      const t = c.lastChild;
      if (c._mabOrigTotal != null && t && t.nodeType === 3) t.textContent = c._mabOrigTotal;
    });
    e.gallery?.updatedCallback?.();
    e.zoomDialog?.updatedCallback?.();
    slideshow?.updatedCallback?.();
    this._galleryEntry = null;
  }

  galleryBgColor(gallery) {
    // With media fit contain the theme paints the letterbox on the photo
    // itself, not on its container, so ask a native photo first and the
    // container after, and the preview's bars match the photo beside it.
    const probes = [
      gallery.querySelector('.product-media-container:not(.mab-preview-container) .product-media__image'),
      gallery.querySelector('.product-media-container'),
      gallery,
    ];
    for (const probe of probes) {
      if (!probe) continue;
      const c = getComputedStyle(probe).backgroundColor;
      if (c && c !== 'rgba(0, 0, 0, 0)' && c !== 'transparent') return c;
    }
    return '#f3eee3';
  }

  drawInnerShadow(ctx, shape, cx, cy, span, color, blur) {
    if (blur <= 0) return;
    ctx.save();
    ctx.clip(shape);
    ctx.shadowColor = color;
    ctx.shadowBlur = blur;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    const inv = new Path2D();
    inv.rect(cx - span * 2, cy - span * 2, span * 4, span * 4);
    inv.addPath(shape);
    ctx.fillStyle = 'rgba(0,0,0,1)';
    ctx.fill(inv, 'evenodd');
    ctx.restore();
  }

  /* ---- add-to-cart gating + second-photo add-on -------------------------- */

  bindAddToCart() {
    this._onAddClickCapture = (event) => this.onAddClickCapture(event);
    this._onCartUpdate = (event) => this.onCartUpdate(event);

    this.form = document.getElementById(this.formId);
    if (!this.form) {
      requestAnimationFrame(() => {
        this.form = document.getElementById(this.formId);
        this.shareErrorMessage();
        // The chips were marked before the form existed, so the id it carries
        // is whatever Liquid rendered rather than what the page is showing.
        this.syncVariant();
        this.updateGate();
      });
    } else {
      this.syncVariant();
    }
    document.addEventListener('click', this._onAddClickCapture, true);
    // When our locket is added and a second photo is set, add its paid line.
    document.addEventListener('cart:update', this._onCartUpdate);
    // The refusal's HTTP status arrives on cart:error, just before the
    // cart:update that says the add failed, so it is noted here and read there.
    this._onCartError = (event) => this.onCartError(event);
    document.addEventListener('cart:error', this._onCartError);
    this.shareErrorMessage();
    this.updateGate();
  }

  /**
   * Notes the HTTP status of a failed add from this block's own form.
   * product-form.js puts it on the event as `status`: a number for an answer
   * Shopify gave, null when the request never got one.
   */
  onCartError(e) {
    const detail = e.detail || {};
    if (!this.formId || detail.sourceId !== this.formId) return;
    const status = detail.data && detail.data.status;
    this._lastAddStatus = typeof status === 'number' ? status : null;
  }

  /**
   * Whether a failed add is a HEIC worth resending as a JPEG, and if so, the
   * start of that resend. Spec criterion 32: the original HEIC goes up first,
   * and only when Shopify refuses that request is it resent once, at full
   * resolution, with `_Photo_source` (and `_Photo2_source`) saying so.
   *
   * The failure path cannot tell a file-type refusal from any other refusal:
   * Shopify's answer names no field, and no HEIC was ever refused in testing
   * because none could be made. So any 4xx from /cart/add while a HEIC is on a
   * carrier is treated as a refusal of the HEIC. Sold out, or a quantity limit,
   * costs one wasted resend and then the same message the buyer gets today. A
   * 5xx, a timeout or a lost answer is not a refusal and is not resent here;
   * product-form.js already retries those itself, and only when the cart says
   * the first one did not land.
   *
   * Once per add, by construction: the resend puts JPEGs on the carriers, so a
   * second refusal finds no HEIC and is reported like any other.
   *
   * UNTESTED against a real HEIC (no HEIC encoder on the machine this was
   * built on, and whether Shopify accepts HEIC as a line file is unverified).
   * The owner proves it with a real iPhone photo on the preview.
   */
  retryHeicAsJpeg(fromSheet) {
    const status = this._lastAddStatus;
    this._lastAddStatus = null;
    if (this.blankMode || this._converting || !this._pendingAdd) return false;
    if (!(status >= 400 && status < 500)) return false;
    const slots = [1, 2].filter((slot) => this.heicOnCarrier(slot));
    if (!slots.length) return false;
    this.resendAsJpeg(slots, fromSheet);
    return true;
  }

  /** True when this slot's carrier on the locket line holds an original HEIC. */
  heicOnCarrier(slot) {
    const s = this.photos[slot];
    if (!s || !s.set || !s.original || s.upload !== s.original || !isHeicFile(s.original)) return false;
    if (slot === 1) return true;
    // The back photo is only on this line when it rides the locket line.
    return !!(this.fileInput2 && !this.fileInput2.disabled);
  }

  async resendAsJpeg(slots, fromSheet) {
    this._converting = true;
    // On a phone the status line lives in the sheet, so the sheet comes back to
    // say what is happening while the photo converts.
    this.openSheetOnPhone();
    this.setBusy(true, this.dataset.msgConverting);
    const held = slots.map((slot) => this.photos[slot]);
    let ok = true;
    try {
      for (const s of held) {
        const out = await fullResJpeg(s.original, this.dataset.heicLib);
        // A copy of any other size would put the crop on the wrong pixels.
        if (!matchesNat(out, s.nat)) {
          console.error('[mab-locket] the converted copy is', out && [out.w, out.h], 'and the crop was made on', s.nat);
          ok = false;
          break;
        }
        s.converted = new File([out.blob], `${s.baseName}.jpg`, { type: 'image/jpeg' });
      }
    } catch (err) {
      console.error('[mab-locket] the conversion for the resend failed', err);
      ok = false;
    } finally {
      this._converting = false;
      this.setBusy(false);
    }

    // The buyer replaced or removed a photo while it converted. What is on the
    // form now is hers to send, so nothing is resent on her behalf.
    if (held.some((s, i) => this.photos[slots[i]] !== s)) {
      this._pendingAdd = false;
      return;
    }
    if (!ok) {
      held.forEach((s) => (s.converted = null));
      // handleAddFailure opens the sheet itself, and an older browser throws on
      // opening a dialog that is already open.
      this.closeSheet();
      this.handleAddFailure();
      return;
    }
    held.forEach((s) => (s.upload = s.converted));
    this.putCarriers();

    if (fromSheet) {
      this.submitFromSheet();
      return;
    }
    this._pendingAdd = true;
    // The sheet was opened to show the conversion; a submit behind a modal
    // would leave the buyer looking at step 3 with nothing happening.
    this.closeSheet();
    const button = this.form && this.form.querySelector('[ref="addToCartButton"]');
    if (this.form && typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
    else if (button) button.click();
  }

  /**
   * Puts each set photo's upload on its carrier, and marks a converted one.
   * The upload is the original File the picker produced, unchanged, unless a
   * refused HEIC was swapped for its JPEG by resendAsJpeg.
   */
  putCarriers() {
    const front = this.photos[1];
    if (front.set && front.upload) {
      const dt = new DataTransfer();
      dt.items.add(front.upload);
      this.fileInput.files = dt.files;
    }
    const back = this.photos[2];
    if (back.set && back.upload) {
      back.file = back.upload;
      if (this.fileInput2) {
        const dt = new DataTransfer();
        dt.items.add(back.upload);
        this.fileInput2.files = dt.files;
      }
    }
    this.applySourceInputs();
  }

  /**
   * `_Photo_source` and `_Photo2_source` ride a line only when that photo's
   * carrier holds a converted JPEG, which happens only in resendAsJpeg. Every
   * other line carries neither, so the properties a normal order has are
   * exactly the ones it had before.
   */
  applySourceInputs() {
    const converted = (slot) => {
      const s = this.photos && this.photos[slot];
      return !this.blankMode && !!(s && s.set && s.upload && s.original && s.upload !== s.original);
    };
    const one = converted(1);
    if (this.sourceInput) {
      this.sourceInput.value = one ? SOURCE_CONVERTED : '';
      this.sourceInput.disabled = !one;
    }
    const two = converted(2) && !!this.fileInput2 && !this.fileInput2.disabled;
    if (this.sourceInput2) {
      this.sourceInput2.value = two ? SOURCE_CONVERTED : '';
      this.sourceInput2.disabled = !two;
    }
  }

  /**
   * The theme draws its own inline error under the button from this attribute.
   * Handing it the block's wording means the two surfaces say one sentence
   * instead of "Error adding to cart" beside a longer, kinder one.
   */
  shareErrorMessage() {
    const owner = this.form && this.form.closest('product-form-component');
    if (owner && this.dataset.msgAddFailed) owner.dataset.addErrorMessage = this.dataset.msgAddFailed;
  }

  onAddClickCapture(e) {
    const target = e.target;
    const btn = target && target.closest && target.closest('[ref="addToCartButton"]');
    if (!btn) return;
    const inMainForm = this.form && this.form.contains(btn);
    const inSticky = !!btn.closest('sticky-add-to-cart');
    if (!inMainForm && !inSticky) return;
    if (this.isReady()) {
      // Every add goes through this button: the page's own, the sticky bar's,
      // and the sheet's, which clicks it. Marking the attempt here rather than
      // in the sheet is what makes a failed desktop add reported too.
      this._pendingAdd = true;
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
    this.nudgeRights();
  }

  onCartUpdate(e) {
    const d = e.detail && e.detail.data;
    if (!d) return;
    const ours = d.source === 'product-form-component' && String(d.productId) === String(this.productId);
    if (ours && d.didError) {
      const fromSheet = this._sheetAdd;
      this._sheetAdd = false;
      // The drawer is showing a line for something the cart refused, and two
      // modal dialogs cannot both be on top. The drawer goes, the stand-in goes
      // with it, and the sheet comes back with the message and the button that
      // sends it again.
      this.closeCartDrawer();
      this.removePendingLine();
      // A refused HEIC gets one resend as a JPEG before anything is reported.
      if (this.retryHeicAsJpeg(fromSheet)) return;
      this.handleAddFailure();
      return;
    }
    if (ours) {
      this._pendingAdd = false;
      // The real line has arrived in the same container, so the stand-in goes.
      // The section morph would take it too, but only when sections HTML rode
      // the event, and this must not depend on that.
      this.removePendingLine();
    }
    // The add came from the sheet, so the tap that started it has to finish
    // somewhere the buyer can see. The shop's own "open the drawer after
    // adding" setting is off and is not this slice's to turn on, since it would
    // change every add on the site; this opens the drawer for the one add that
    // began inside the sheet.
    if (ours && this._sheetAdd) {
      this._sheetAdd = false;
      this.openCartDrawer();
    }
    if (this.addonVariant && !this.blankMode && ours && this.photos[2].set && this.pairId) {
      const qty = Number(d.itemCount) || 1;
      this.maybeAddSecondPhotoLine(qty);
    }
  }

  async maybeAddSecondPhotoLine(qty) {
    // The only way into the paid add-on line, and it needs the owner to have
    // set an add-on product in theme settings. With that blank, which is how it
    // ships, the back photo rides the locket line and this never runs.
    if (!this.addonVariant) return;
    // A blank locket has no photos at all, so it can never owe for a second one.
    if (this.blankMode) return;
    if (this._addonInFlight) return;
    const slot = this.photos[2];
    if (!slot.set || !slot.file) return;
    this._addonInFlight = true;
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const already = (cart.items || []).some(
        (it) => it.properties && it.properties._photo_pair_id === this.pairId && it.properties._side === 'back'
      );
      if (already) return;

      const fd = new FormData();
      fd.append('id', this.addonVariant);
      fd.append('quantity', String(qty));
      // The original, under its own name, as on the locket line (changed by
      // the 4 Oct kickoff: originals on the order). Renaming it -back.jpg
      // would call a HEIC a JPEG. This dormant path has no HEIC resend of its
      // own: a refused HEIC here is logged below, like any failure of this line.
      fd.append('properties[_Photo2]', slot.file);
      fd.append('properties[_crop2]', slot.payload || '');
      fd.append('properties[_photo_pair_id]', this.pairId);
      fd.append('properties[_locket_photo_count]', '2');
      fd.append('properties[_side]', 'back');
      const sectionId = this.cartSectionId();
      if (sectionId) fd.append('sections', sectionId);

      const resp = await fetch(CART_ADD_URL, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: fd,
      }).then((r) => r.json());

      // Refresh the drawer/icon. cart-items-component re-fetches the section
      // when no sections HTML rides the event, so this is safe either way.
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { data: { source: 'mab-locket-addon', sections: resp && resp.sections }, resource: {} },
        })
      );
    } catch (err) {
      console.error('[mab-locket] failed to add second photo line', err);
    } finally {
      this._addonInFlight = false;
    }
  }

  cartSectionId() {
    const el = document.querySelector('cart-items-component[data-section-id], cart-items-component[section-id]');
    return el ? el.dataset.sectionId || el.getAttribute('section-id') : '';
  }

  isReady() {
    // A blank locket has no photo to wait for and no rights to confirm, so
    // there is nothing left to gate.
    if (this.blankMode) return true;
    return this.fileInput.files.length > 0 && !!this.cropInput.value && !!(this.rights && this.rights.checked);
  }

  updateGate() {
    const ready = this.isReady();
    const hasPhoto = this.fileInput.files.length > 0 && !!this.cropInput.value;
    if (this.helper) {
      let msg = '';
      if (!this.blankMode) {
        if (!hasPhoto) msg = this.dataset.msgNeedPhoto || '';
        else if (this.rights && !this.rights.checked) msg = this.dataset.msgNeedRights || '';
      }
      this.helper.textContent = msg;
      this.helper.hidden = !msg;
    }
    // The highlight on the rights box is the explanation for a disabled Add to
    // cart, so it lasts exactly as long as the thing it explains. It used to be
    // a one-shot nudge, applied when a tap was refused and taken off by the
    // next change event, which meant unticking the box left the button
    // unavailable with nothing pointing at why.
    const row = this.querySelector('.mab-locket__rights');
    if (row) {
      const wanted = !this.blankMode && hasPhoto && !!(this.rights && !this.rights.checked);
      row.classList.toggle('mab-locket__rights--nudged', wanted);
    }
    this.gateVisual(this.form && this.form.querySelector('[ref="addToCartButton"]'), ready);
    this.gateVisual(document.querySelector('sticky-add-to-cart [ref="addToCartButton"]'), ready);
    // The sheet's own bar reads the same gate, so ticking the rights box turns
    // a disabled Add to cart into a live one without closing anything.
    this.syncSheet();
    this.announceGate(ready);
  }

  /**
   * Tells anything that cares whether the gate is open, so it does not have to
   * poll. Additive: the gate itself, the native required attributes and the
   * capture click handler are unchanged and remain the enforcement. This event is
   * for wording only, which is why it carries the label.
   *
   * Fires on a change of either the gate or the wording, so a listener can
   * trust it as an edge. The wording matters on its own: placing a photo
   * without ticking the rights box leaves the gate shut and changes what the
   * bar should say, and an event keyed only on the gate would never fire.
   */
  announceGate(ready) {
    const label = this.gatedLabel();
    const edge = `${ready}|${label}`;
    if (this._gateAnnounced === edge) return;
    this._gateAnnounced = edge;
    const hasPhoto = this.fileInput.files.length > 0 && !!this.cropInput.value;
    let reason = 'ready';
    if (this.blankMode) reason = 'blank';
    else if (!hasPhoto) reason = 'photo';
    else if (this.rights && !this.rights.checked) reason = 'rights';
    document.dispatchEvent(
      new CustomEvent('mab:locket:gate', {
        detail: {
          ready,
          productId: this.productId,
          reason,
          // The sticky bar shows whatever this says, so it has to name the
          // thing that is actually missing.
          label,
        },
      })
    );
  }

  get reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * The last thing standing between the buyer and her cart is usually a
   * checkbox she is not looking at, so point at it rather than only refusing.
   *
   * This is the desktop and sticky-bar path now. On step 3 in the sheet the
   * button is genuinely disabled and the box is already highlighted, so there
   * is no refused tap to answer; this runs when the page's own add-to-cart
   * button is clicked with the gate shut.
   */
  nudgeRights() {
    if (!this.photos[1].set || !this.cropInput.value) {
      this.showError(this.dataset.msgNeedPhoto);
      return;
    }
    this.showError(this.dataset.msgNeedRights);
    if (!this.rights) return;
    // Only scroll to it when it is somewhere that scrolls. In the sheet's bar
    // it is already on the screen and has no scrolling ancestor short of the
    // document, so asking would jump the page behind the dialog instead.
    const inBar = !!(this.barRights && this.barRights.contains(this.rights));
    if (!inBar) {
      this.rights.scrollIntoView({ block: 'center', behavior: this.reducedMotion ? 'auto' : 'smooth' });
    }
    this.rights.focus({ preventScroll: true });
    // The ring itself is updateGate's, which puts it on for as long as the box
    // is the thing missing and takes it off when she answers. This used to add
    // the class here and bind a one-shot change listener to remove it, so
    // unticking the box after ticking it left no ring at all.
    this.updateGate();
  }

  gateVisual(btn, ready) {
    if (!btn) return;
    btn.classList.toggle('mab-atc-gated', !ready);
    const helperId = this.helper && this.helper.id;
    if (ready) {
      btn.removeAttribute('aria-disabled');
      if (helperId && btn.getAttribute('aria-describedby') === helperId) {
        btn.removeAttribute('aria-describedby');
      }
    } else {
      btn.setAttribute('aria-disabled', 'true');
      // On a phone the sticky button is icon-only while gated, so its name is
      // "Add your photo" with no reason attached. Pointing at the helper line
      // makes the reason follow the name.
      if (helperId) btn.setAttribute('aria-describedby', helperId);
    }
  }

  ensurePairId() {
    // A pair token only means anything when there are two lines to pair. With
    // no add-on product the second photo rides the locket line, so nothing is
    // paired and nothing is written.
    if (!this.addonVariant) return;
    if (this.pairId) return;
    this.pairId =
      (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
      `p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    if (this.pairInput) this.pairInput.value = this.pairId;
  }

  clearPairId() {
    this.pairId = null;
    if (this.pairInput) this.pairInput.value = '';
  }

  /* ---- file selection + decode ------------------------------------------ */

  onPick(slot) {
    const input = slot === 2 && this.picker2 ? this.picker2 : this.fileInput;
    const file = input.files && input.files[0];
    return this.handleFile(file, slot);
  }

  // Shared by the file picker and by a drop onto the card. For slot 1 the chosen
  // file is never left in the form input: `required` must mean "a cropped photo
  // is attached", so applyCrop puts the same File in later, unchanged. For slot
  // 2 the file is held in JS and rides the add-on line.
  async handleFile(file, slot) {
    this.editing = slot;
    if (slot === 1) this.clearMainFile();
    else this.clearSlot(2);
    this.updateGate();
    if (!file) return;

    // On a phone the picker is opened from the button on the page, with the
    // sheet still closed, and everything that answers back lives inside the
    // flow: the error line, the polite status line that says a HEIC is
    // converting, and the undo row. The flow is already inside the closed
    // dialog, so a refused file would say so where nobody could see it. Open
    // the sheet before the decode, not after it: it shows step 1 until the
    // photo is in hand and then becomes step 2.
    this.openSheetOnPhone();

    const s = this.photos[slot];
    s.baseName =
      (file.name || 'photo')
        .replace(/\.[^.]+$/, '')
        .replace(/[^\w-]+/g, '-')
        .slice(0, 40) || 'photo';

    const isHeic = isHeicFile(file);
    // Whether this is a photo is decided by the decode below, not by a list of
    // types up here. A list of five formats turned a GIF away with a sentence
    // that said it was not a photo, which is not true of a GIF; and a type list
    // never saw the file a browser reported no type for at all. The one thing
    // refused before the decode is an SVG, which some browsers will happily
    // open and which is a drawing rather than a photograph.
    if (isSvgFile(file)) {
      this.showError(this.dataset.msgUnsupported);
      return;
    }

    this.hideError();
    // Now there is a photo, so the composite will be drawn: start the base
    // photo in parallel with the decode rather than on page load.
    const baseReady = this.ensureBase();
    s.bitmap = null;
    s.nat = null;
    s.original = null;
    s.upload = null;
    s.converted = null;

    try {
      let bmp = await this.decode(file);
      if (!bmp && isHeic) {
        // Waiting, not warning: this goes in a polite status line, and the bar
        // stops offering an action it cannot perform yet.
        this.setBusy(true, this.dataset.msgConverting);
        const jpeg = await this.heicToJpeg(file);
        bmp = await this.decode(jpeg);
      }
      if (!bmp) {
        // Nothing decoded, so this is where a file that is not a photo is
        // turned away. Which sentence depends on what she chose: a photo that
        // will not open is a broken photo and says so, and anything else gets
        // the owner's wording, which is about photos and not about formats.
        this.showError(notPhotoMessage(file, this.dataset.msgUnsupported, this.dataset.msgDecodeFailed));
        return;
      }
      // Measured at the original's upright size, which is what is uploaded and
      // what the print is made from (changed by the 4 Oct kickoff: originals on
      // the order). It used to be measured at the 1500 px copy that was sent.
      const origW = bmp.width;
      const origH = bmp.height;
      if (isTooSmall(origW, origH, this.minPx)) {
        // Refused, so let the pixels go rather than holding them for a crop
        // that will never happen.
        if (typeof bmp.close === 'function') bmp.close();
        this.showError(this.tooSmallMessage(origW, origH));
        // Stay where the buyer was: cropping if the other face is mid-edit,
        // else ready if a first photo exists, else empty.
        this.setState(this.anyCrop() ? 'ready' : 'empty');
        return;
      }
      // The crop box has to be the shape of the window before it is drawn, and
      // Liquid usually already said what that is. This waits only on a product
      // whose base dimensions did not resolve there, and it says so rather than
      // freezing the bar: the decode has already finished by now.
      if (this.needsBaseDims()) {
        this.setBusy(true, this.dataset.msgConverting);
        await baseReady;
      }
      // The crop box draws a copy at most 1500 px on its long side, and the
      // full decode is let go here. The File itself is kept untouched: it is
      // what goes on the order, and `nat` is its upright size.
      const work = await workingCopy(bmp);
      s.nat = work.nat;
      s.original = file;
      s.upload = file;
      bmp = work.bitmap;
      s.bitmap = bmp;
      this.applyCropAspect(slot);
      this.setState('cropping');
      this.setupImage();
      // Only now. The replacement is real, it decoded, and it is big enough, so
      // the photo it replaces is finally safe to let go of. Dropping the stash
      // at pick time meant a Replace that was refused, for being too small or
      // for not opening, destroyed the crop the buyer already had.
      this.dropRemoved();
    } catch (err) {
      console.error('[mab-locket] decode failed', err);
      // Same choice as the quiet failure above: a decode that throws on a
      // photo is a broken photo, and one that throws on anything else is not a
      // photo at all.
      this.showError(notPhotoMessage(file, this.dataset.msgUnsupported, this.dataset.msgDecodeFailed));
    } finally {
      this.setBusy(false);
    }
  }

  /**
   * The refusal says what the photo actually is, so the buyer can tell whether
   * she has a bigger one, instead of being told only that this is not enough.
   * The wording is the block setting; the numbers go in through the core.
   */
  tooSmallMessage(width, height) {
    return tooSmallMessage(this.dataset.msgTooSmall, width, height);
  }

  /**
   * A wait, announced politely and with the bar's action taken away, because
   * the bar cannot do anything until the decode finishes.
   */
  setBusy(busy, message) {
    this._busy = !!busy;
    if (this.statusEl) {
      // Unhide before writing: text written into a hidden live region is not
      // announced, and the class comes off because this one is meant to be read
      // as well as heard.
      this.statusEl.classList.remove('mab-locket__status--quiet');
      this.statusEl.hidden = !busy;
      this.statusEl.textContent = busy ? message || '' : '';
    }
    // One owner for the disabled state, so a decode that finishes cannot hand
    // an ungated button back to a buyer who has not ticked the rights box, and
    // the gate cannot take a button away from a decode that is still running.
    if (this.barAction) this.syncSheet();
  }

  // The decode and the HEIC fallback are the core's. The library's address is
  // the one thing this block knows that the core does not.
  decode(blob) {
    return decode(blob);
  }

  heicToJpeg(file) {
    return heicToJpeg(file, this.dataset.heicLib);
  }

  /* ---- cropper ----------------------------------------------------------- */

  setupImage() {
    const s = this.cur();
    const vw = this.viewport.clientWidth;
    const vh = this.viewport.clientHeight;
    s.minScale = coverScale(s.bitmap.width, s.bitmap.height, vw, vh);
    this.zoomInput.value = '1';
    Object.assign(s.view, centredView(s.bitmap.width, s.bitmap.height, vw, vh));
    this.render();
  }

  // The view is changed in place rather than replaced, so anything holding
  // the slot sees the same object it always did.
  clamp() {
    const s = this.cur();
    const vw = this.viewport.clientWidth;
    const vh = this.viewport.clientHeight;
    Object.assign(s.view, clampView(s.view, s.bitmap.width, s.bitmap.height, vw, vh));
  }

  // The photo is painted twice into identically-sized canvases: the bleed layer
  // spans the whole frame (CSS drops it to low opacity), the crop layer sits
  // above it inside the shaped mask at full strength. Same transform.
  render() {
    const s = this.cur();
    if (!s.bitmap) return;
    const vw = this.viewport.clientWidth;
    const vh = this.viewport.clientHeight;
    if (!vw || !vh) return;
    const dpr = window.devicePixelRatio || 1;

    [this.bleedCanvas, this.cropCanvas].forEach((cv) => {
      if (cv) paintView(cv, s.bitmap, s.view, vw, vh, dpr);
    });
  }

  onZoom() {
    const s = this.cur();
    if (!s.bitmap) return;
    const vw = this.viewport.clientWidth;
    const vh = this.viewport.clientHeight;
    Object.assign(s.view, zoomView(s.view, s.minScale * (parseFloat(this.zoomInput.value) || 1), vw, vh));
    this.clamp();
    this.render();
  }

  // Pan and pinch live on the viewport, not the clipped canvas, so a drag that
  // starts in the faint bleed area outside the aperture still works.
  setupPointer() {
    const el = this.viewport;
    const pointers = new Map();
    let last = null;

    el.addEventListener('pointerdown', (e) => {
      const s = this.cur();
      if (!s.bitmap) return;
      el.setPointerCapture(e.pointerId);
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 1) last = { x: e.clientX, y: e.clientY };
    });

    el.addEventListener('pointermove', (e) => {
      const s = this.cur();
      if (!pointers.has(e.pointerId) || !s.bitmap) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size >= 2) {
        this.pinch([...pointers.values()]);
        return;
      }
      if (last) {
        s.view.x += e.clientX - last.x;
        s.view.y += e.clientY - last.y;
        last = { x: e.clientX, y: e.clientY };
        this.clamp();
        this.render();
      }
    });

    const end = (e) => {
      pointers.delete(e.pointerId);
      if (pointers.size < 2) this._pinchBase = null;
      if (pointers.size === 0) last = null;
      else {
        const p = pointers.values().next().value;
        last = { x: p.x, y: p.y };
      }
    };
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);

    el.addEventListener(
      'wheel',
      (e) => {
        if (!this.cur().bitmap) return;
        e.preventDefault();
        this.zoomInput.value = String(wheelZoom(parseFloat(this.zoomInput.value) || 1, e.deltaY));
        this.onZoom();
      },
      { passive: false }
    );
  }

  pinch(pts) {
    const step = pinchZoom(this._pinchBase, pts, parseFloat(this.zoomInput.value) || 1);
    this._pinchBase = step.base;
    if (step.zoom === null) return;
    this.zoomInput.value = String(step.zoom);
    this.onZoom();
  }

  /* ---- apply crop + write payload ---------------------------------------- */

  async applyCrop() {
    const slot = this.editing;
    const s = this.photos[slot];
    if (!s.bitmap) return;
    const vw = this.viewport.clientWidth;
    const vh = this.viewport.clientHeight;
    if (!vw || !vh || !s.view.scale) return;

    const crop = cropFromView(s.view, s.bitmap.width, s.bitmap.height, vw, vh);
    const { x, y, w, h } = crop;

    // The crop is taken on the working copy, so it is scaled up to the
    // original's pixels before it is judged: those are the pixels the print is
    // made from (changed by the 4 Oct kickoff: originals on the order).
    const ratio = s.nat[0] / s.bitmap.width;
    if (isTooSmall(w * ratio, h * ratio, this.minPx)) {
      this.showError(this.tooSmallMessage(w * ratio, h * ratio));
      return;
    }

    // TODO(owner): _crop.aspect used to be the block setting's [w, h] pair and
    // is now the one number the crop box was actually drawn at, per window.
    // Orders placed before this slice carry the pair [1, 1]; orders after it
    // carry a single float. Both are screen ratios and neither is a
    // measurement; anyone who reads this number as a recess ratio puts a 14
    // percent error into a cut, because the aperture was fitted by eye over a
    // photograph.
    //
    // The only millimetres in _crop are aperture_mm, and they appear only for
    // a locket the theme's shape registry (mab-locket-shapes.js) marks as
    // measured, which today is the Oval Locket alone. They echo that registry
    // and nothing else: Locket Print Studio's own registry
    // (src/shapes/registry.ts, widthMm and heightMm) stays the print
    // authority, the studio does not read _crop, and a printed size never
    // comes from a Shopify metafield. An unmeasured locket carries no
    // aperture_mm key at all. buildCrop in mab-photo-core.js writes the whole
    // payload, the same builder the locket prints page uses.
    s.payload = JSON.stringify(
      buildCrop(
        {
          shape: this.shape,
          aspect: this.windowAspect(slot),
          // The original's upright size, not the working copy's (changed by
          // the 4 Oct kickoff: originals on the order). rect is a fraction, so
          // taking it on the working copy gives the same numbers.
          nat: [s.nat[0], s.nat[1]],
          rect: normaliseRect(crop, s.bitmap.width, s.bitmap.height),
        },
        this.shapeEntry,
        this.handle
      )
    );
    s.crop = { x, y, w, h };
    s.set = true;

    // The carrier gets s.upload, which is the File the picker produced, byte
    // for byte: no crop, resize or re-encode (spec criterion 31). It only ever
    // differs from s.original after Shopify refused a HEIC (resendAsJpeg).
    //
    // TODO(owner): an original keeps all of its EXIF, GPS location included,
    // so where a photo was taken reaches the order and the admin. The kickoff
    // forbids touching the bytes, so nothing strips it here; whether to strip
    // it, tell the buyer, or accept it is the owner's decision (spec
    // criterion 36).
    if (slot === 1) {
      this.cropInput.value = s.payload;
      const dt = new DataTransfer();
      dt.items.add(s.upload);
      this.fileInput.files = dt.files;
      this.applySourceInputs();
    } else {
      s.file = s.upload;
      if (this.fileInput2) {
        // No add-on line to ride, so the back photo goes on the locket line.
        const dt = new DataTransfer();
        dt.items.add(s.file);
        this.fileInput2.files = dt.files;
        if (this.cropInput2) this.cropInput2.value = s.payload;
      }
      // Only writes a token when there is an add-on line to pair with.
      this.ensurePairId();
      this.applySecondInputs();
    }

    this.hideError();
    await this.enterReady();
    // A placed photo selects the variant that charges for one, without the
    // buyer having to find the chip and say so twice. Front face or back: the
    // ten dollars covers both, so the back photo finding its way on when the
    // choice somehow says none puts the choice right rather than charging again.
    this.choosePhoto(true);
    this.updateGate();
  }

  // Choose the preview mode and render it. Composite when a base image is
  // available; otherwise the Phase 1 bezel preview.
  async enterReady() {
    let useComposite = !!this.dataset.baseImage;
    if (useComposite) {
      const ok = await this.ensureBase();
      useComposite = ok && !!this.baseImg;
    }
    this.dataset.previewMode = useComposite ? 'composite' : 'bezel';
    if (this.galleryEnabled && useComposite) this.classList.add('mab-locket--in-gallery');
    this.setState('ready');

    if (this.photos[1].set) this.drawThumb(1);
    if (this.photos[2].set) this.drawThumb(2);
    this.updateSecondUI();

    if (useComposite) {
      this.drawComposite();
      this.maybeShowTuner();
      if (this.galleryEnabled) this.syncGallery();
    } else if (this.photos[1].set) {
      this.drawPreview(this.photos[1].crop);
    }
  }

  // Front-face-only bezel preview (no base image). The second photo has no
  // believable single-window preview here, so it is not offered in bezel mode.
  drawPreview(crop) {
    if (!crop) return;
    const box = this.previewCanvas.parentElement;
    const pw = box.clientWidth;
    const ph = box.clientHeight;
    if (!pw || !ph) return;
    const { x, y, w, h } = crop;
    const dpr = window.devicePixelRatio || 1;
    const c = this.previewCanvas;
    c.width = Math.round(pw * dpr);
    c.height = Math.round(ph * dpr);
    const ctx = c.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, pw, ph);
    ctx.drawImage(this.photos[1].bitmap, x, y, w, h, 0, 0, pw, ph);
  }

  // The 36px shaped thumbnail per set photo. Painted at a fixed backing size so
  // it is never blank on a container that has not been measured yet. It draws
  // the same crop the composite draws, so it takes the same window shape: a
  // square chip would squeeze the face exactly as the old square crop box did,
  // at 36px, where it is hardest to notice and easiest to believe.
  drawThumb(slot) {
    const canvas = this.thumbCanvas[slot];
    const s = this.photos[slot];
    if (!canvas || !s.bitmap || !s.crop) return;
    const { x, y, w, h } = s.crop;
    const ratio = this.windowAspect(slot);
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    // The longer side stays 36px, so a chip is never bigger than the square one
    // it replaces and two chips of different shapes still line up.
    const cssW = ratio >= 1 ? 36 : 36 * ratio;
    const cssH = ratio >= 1 ? 36 / ratio : 36;
    const box = canvas.parentElement;
    if (box) {
      box.style.width = `${cssW}px`;
      box.style.height = `${cssH}px`;
    }
    const bw = Math.max(1, Math.round(cssW * dpr));
    const bh = Math.max(1, Math.round(cssH * dpr));
    canvas.width = bw;
    canvas.height = bh;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, bw, bh);
    ctx.drawImage(s.bitmap, x, y, w, h, 0, 0, bw, bh);
  }

  // Offer state: none (no second window / bezel), offer (available, empty), set.
  updateSecondUI() {
    let mode = 'none';
    if (this.hasSecondWindow && this.dataset.previewMode === 'composite') {
      mode = this.photos[2].set ? 'set' : 'offer';
    }
    this.dataset.second = mode;
    if (this.offerBtn) this.offerBtn.hidden = mode !== 'offer';
  }

  /* ---- aperture tuner (owner-only, gated) -------------------------------- */

  setupTuner() {
    if (!this.tuner) return;
    this.tuneWin = 0; // which window the sliders drive
    this.tunerSliders = [...this.tuner.querySelectorAll('[data-ap]')];
    this.tunerSliders.forEach((input) => {
      input.addEventListener('input', () => {
        const key = input.dataset.ap;
        const win = this.windows[this.tuneWin];
        if (!win) return;
        win[key] = parseFloat(input.value);
        this.drawComposite();
        this.refreshTunerJson();
        if (this.galleryEnabled && this.anyCrop()) this.syncGallery();
      });
    });

    this.tuner.querySelectorAll('.mab-locket__tuner-win').forEach((btn) => {
      btn.addEventListener('click', () => this.selectTuneWindow(parseInt(btn.dataset.win, 10)));
    });
    this.q('tunerAddWin')?.addEventListener('click', () => this.addTuneWindow());
    this.q('tunerRemoveWin')?.addEventListener('click', () => this.removeTuneWindow());
    if (this.tunerCopy) this.tunerCopy.addEventListener('click', () => this.copyTunerJson());
  }

  selectTuneWindow(win) {
    if (!this.windows[win]) return;
    this.tuneWin = win;
    this.tuner.querySelectorAll('.mab-locket__tuner-win').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(parseInt(btn.dataset.win, 10) === win));
    });
    this.syncTunerSliders();
  }

  addTuneWindow() {
    if (this.windows.length >= 2) return;
    const a = this.windows[0];
    // Seed the second window mirrored to the other side of the base image.
    this.windows.push(Object.assign({}, a, { cx: round4(Math.min(0.9, 1 - a.cx)) }));
    this.hasSecondWindow = true;
    this.refreshTunerWindows();
    this.selectTuneWindow(1);
    this.drawComposite();
    this.updateSecondUI();
    if (this.galleryEnabled && this.anyCrop()) this.syncGallery();
  }

  removeTuneWindow() {
    if (this.windows.length < 2) return;
    // Removing a window the buyer already filled would strand that photo.
    if (this.photos[2].set) this.removeSecond();
    this.windows.pop();
    this.hasSecondWindow = false;
    this.tuneWin = 0;
    this.refreshTunerWindows();
    this.selectTuneWindow(0);
    this.drawComposite();
    this.updateSecondUI();
    if (this.galleryEnabled && this.anyCrop()) this.syncGallery();
  }

  refreshTunerWindows() {
    const two = this.windows.length >= 2;
    const win2 = this.q('tunerWin2');
    const add = this.q('tunerAddWin');
    const rem = this.q('tunerRemoveWin');
    if (win2) win2.hidden = !two;
    if (add) add.hidden = two;
    if (rem) rem.hidden = !two;
  }

  syncTunerSliders() {
    const win = this.windows[this.tuneWin];
    if (!win) return;
    this.tunerSliders.forEach((input) => {
      const key = input.dataset.ap;
      if (win[key] != null) input.value = win[key];
    });
    this.refreshTunerJson();
  }

  maybeShowTuner() {
    if (!this.tuner) return;
    const show = this.tuningEnabled && this.dataset.previewMode === 'composite';
    this.tuner.hidden = !show;
    if (show) {
      this.refreshTunerWindows();
      this.selectTuneWindow(this.tuneWin || 0);
    }
  }

  apertureJSON() {
    const one = (a) => ({
      cx: round4(a.cx),
      cy: round4(a.cy),
      w: round4(a.w),
      h: round4(a.h),
      rot: Math.round(a.rot * 10) / 10,
      shadow: Math.round(a.shadow),
      feather: Math.round(a.feather),
    });
    // Two windows copy as an array; one window keeps the bare-object form so an
    // existing single-window product's metafield shape is undisturbed.
    if (this.windows.length >= 2) return JSON.stringify(this.windows.map(one));
    return JSON.stringify(one(this.windows[0]));
  }

  refreshTunerJson() {
    if (this.tunerJson) this.tunerJson.value = this.apertureJSON();
  }

  copyTunerJson() {
    const text = this.apertureJSON();
    const done = () => {
      const label = this.dataset.tuneCopied || 'Copied';
      const prev = this.tunerCopy.textContent;
      this.tunerCopy.textContent = label;
      setTimeout(() => (this.tunerCopy.textContent = prev), 1600);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done, () => {
        this.tunerJson.select();
        done();
      });
    } else {
      this.tunerJson.select();
      try {
        document.execCommand('copy');
      } catch (_) {
        /* no-op */
      }
      done();
    }
  }

  /* ---- state + helpers --------------------------------------------------- */

  setState(state) {
    this.stage.dataset.state = state;
    this.dataset.state = state;
    this.step = state === 'ready' ? 3 : state === 'cropping' ? 2 : 1;
    this.syncSheet();
  }

  /**
   * An ImageBitmap holds its pixels outside the JS heap and is not collected
   * just because the reference went away. A 12 megapixel phone photo is about
   * 48 MB of it, so five goes at Replace was a quarter of a gigabyte held on a
   * device that has no spare.
   */
  releaseSlot(slot) {
    const held = this.photos[slot];
    if (!held || !held.bitmap) return;
    // A slot being held for Undo is not a slot being thrown away.
    if (this._undo && (this._undo.one === held || this._undo.two === held)) return;
    if (typeof held.bitmap.close === 'function') held.bitmap.close();
  }

  /**
   * Remove is one tap and it wipes a crop the buyer may have spent a minute on,
   * so the last one is held for the length of the visit. Choosing another photo
   * drops it: at that point she has said what she wants instead.
   */
  stashRemoved(note) {
    if (!this.photos[1].set) return;
    this._undo = { one: this.photos[1], two: this.photos[2], pairId: this.pairId };
    if (this.undoText) this.undoText.textContent = note || this.dataset.undoNote || '';
    this.showUndo(true);
  }

  dropRemoved() {
    if (!this._undo) return;
    const stash = this._undo;
    this._undo = null;
    for (const held of [stash.one, stash.two]) {
      if (held && held.bitmap && typeof held.bitmap.close === 'function') held.bitmap.close();
    }
    this.showUndo(false);
  }

  showUndo(on) {
    if (this.undoEl) this.undoEl.hidden = !on;
  }

  restoreRemoved() {
    const stash = this._undo;
    if (!stash) return;
    this._undo = null;
    this.showUndo(false);

    this.photos[1] = stash.one;
    this.photos[2] = stash.two;
    this.pairId = stash.pairId;
    if (this.pairInput) this.pairInput.value = this.pairId || '';

    // The form inputs were cleared with the state, so put them back exactly:
    // the same File that was on the carrier, the original or, after a refused
    // HEIC, its JPEG.
    const front = stash.one;
    if (front.upload) {
      const dt = new DataTransfer();
      dt.items.add(front.upload);
      this.fileInput.files = dt.files;
    }
    this.cropInput.value = front.payload || '';
    const back = stash.two;
    if (back && back.set && back.file && this.fileInput2) {
      const dt = new DataTransfer();
      dt.items.add(back.file);
      this.fileInput2.files = dt.files;
      if (this.cropInput2) this.cropInput2.value = back.payload || '';
    }
    this.applySecondInputs();
    // The photo is back on the order, so the variant that charges for one is too.
    this.choosePhoto(true);

    this.editing = 1;
    this.hideError();
    this.enterReady();
    this.updateGate();
  }

  clearMainFile() {
    const dt = new DataTransfer();
    this.fileInput.files = dt.files;
    this.cropInput.value = '';
    this.releaseSlot(1);
    this.photos[1] = Object.assign(makeSlot(), { baseName: this.photos[1].baseName });
    this.applySourceInputs();
  }

  // Clear one slot's working state (used for slot 2, and by replace flows).
  clearSlot(slot) {
    this.releaseSlot(slot);
    this.photos[slot] = makeSlot();
    const empty = new DataTransfer();
    if (this.picker2) this.picker2.files = empty.files;
    if (this.fileInput2) this.fileInput2.files = empty.files;
    if (this.cropInput2) this.cropInput2.value = '';
    this.applySecondInputs();
  }

  removeSecond() {
    this.clearSlot(2);
    this.clearPairId();
    this.editing = 1;
    // Redraw the composite/gallery with the front face only.
    if (this.dataset.previewMode === 'composite') {
      this.drawComposite();
      if (this.galleryEnabled) this.syncGallery();
    }
    this.updateSecondUI();
    this.setState('ready');
  }

  /**
   * @param {{note?: string, announce?: boolean, focusUndo?: boolean}} [options]
   */
  reset(options) {
    const { note, announce, focusUndo, keepChoice } = options || {};
    this.stashRemoved(note);
    this.clearMainFile();
    this.clearSlot(2);
    this.clearPairId();
    this.editing = 1;
    this.hideError();
    if (this.tuner) this.tuner.hidden = true;
    if (this.galleryEnabled) this.removeGallery();
    this.dataset.second = 'none';
    this.setState('empty');
    // The photo is off the order, so the order goes back on the variant that
    // does not charge for one. Not on a replace: the picker is opening on the
    // next line and the price would drop and climb again in front of the buyer
    // for a photo that never left.
    if (!keepChoice) this.choosePhoto(false, { keepStash: true });
    this.updateGate();

    // The button that was pressed no longer exists, so without this focus
    // lands on the body and the Undo row arrives with nobody looking at it.
    if (this._undo && announce) this.announceStatus(this.undoText ? this.undoText.textContent : '');
    if (this._undo && focusUndo) {
      const undoButton = this.undoEl && this.undoEl.querySelector('[data-act="undo"]');
      // offsetParent is null inside a closed sheet, and focusing something that
      // is not rendered moves focus to the body, which is worse than leaving it.
      //
      // The button the buyer pressed was inside the photo panel, which blank
      // mode stops rendering, so there is always something to move focus to
      // here. Undo first, because it is the thing she is most likely to want a
      // second after pressing Remove; the marked photo chip otherwise, which is
      // the control that just changed and is never hidden.
      const chip = this.photoGroup && this.photoGroup.querySelector('[aria-checked="true"]');
      const target = undoButton && undoButton.offsetParent !== null ? undoButton : chip;
      if (target && target.offsetParent !== null) target.focus({ preventScroll: true });
    }
  }

  /**
   * A one-off sentence through the polite status line. The Undo row already
   * says it on screen, so this copy is hidden to the eye: the point is that it
   * is heard. Cleared first, so the same sentence twice is announced twice.
   */
  announceStatus(message) {
    if (!this.statusEl || !message) return;
    this.statusEl.classList.add('mab-locket__status--quiet');
    this.statusEl.hidden = false;
    this.statusEl.textContent = '';
    setTimeout(() => {
      this.statusEl.textContent = message;
    }, 60);
  }

  onResize() {
    const state = this.stage.dataset.state;
    if (state === 'cropping') {
      const s = this.cur();
      if (!s.bitmap) return;
      const vw = this.viewport.clientWidth;
      const vh = this.viewport.clientHeight;
      s.minScale = coverScale(s.bitmap.width, s.bitmap.height, vw, vh);
      if (s.view.scale < s.minScale) s.view.scale = s.minScale;
      this.clamp();
      this.render();
    } else if (state === 'ready') {
      if (this.dataset.previewMode === 'composite') {
        this.drawComposite();
      } else if (this.photos[1].set) {
        this.drawPreview(this.photos[1].crop);
      }
    }
    if (this.galleryEnabled && this._galleryEntry) this.paintGallery(this._galleryEntry);
  }

  showError(message, info) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.classList.toggle('mab-locket__error--info', !!info);
    this.errorEl.hidden = !message;
    // On a phone the error line lives inside the sheet. A decode that finishes
    // after the buyer pressed Escape would otherwise write its message where
    // nobody can read it, and she would be left with a card that says nothing
    // happened.
    if (message && this.phone && this.phone.matches && this.sheet && !this.sheet.open) {
      this.openSheet();
    }
  }

  hideError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
    this.errorEl.classList.remove('mab-locket__error--info');
  }
}

function makeSlot() {
  return {
    bitmap: null, // the working copy, at most MAX_EDGE on its long side
    nat: null, // the original's upright [width, height]
    original: null, // the File the picker produced, never changed
    upload: null, // the File on the carrier: original, or its JPEG after a refused HEIC
    converted: null, // that JPEG while resendAsJpeg is making it
    crop: null, // { x, y, w, h } in working-copy pixels, for composite + thumb
    payload: null, // the _crop / _crop2 JSON string
    file: null, // slot 2 only: the File that rides the add-on line
    view: { scale: 1, x: 0, y: 0 },
    minScale: 1,
    baseName: 'photo',
    set: false,
  };
}

function escapeHtml(str) {
  return String(str == null ? '' : str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * A width over height ratio as the theme writes it, either a plain number from
 * media.aspect_ratio or a fraction such as the gallery setting's "2/1". Zero
 * when there is nothing usable, so the caller falls back.
 */
function parseRatio(str) {
  const parts = String(str == null ? '' : str)
    .split('/')
    .map((part) => parseFloat(part));
  const value = parts.length === 2 ? parts[0] / parts[1] : parts[0];
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function safeJSON(str) {
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch (_) {
    return null;
  }
}

// A variant row's featured media id as a string of digits, or null. Digits
// only, because it goes into an attribute selector and a stray quote in it
// would throw rather than find nothing.
function mediaOf(row) {
  const id = row && row.media != null ? String(row.media) : '';
  return /^\d+$/.test(id) ? id : null;
}

// Normalize an aperture value to an array of windows: an array passes through,
// a bare object becomes a one-element array, anything else becomes empty.
function asWindows(val) {
  if (Array.isArray(val)) return val.filter((v) => v && typeof v === 'object');
  if (val && typeof val === 'object') return [val];
  return [];
}

// Keep only recognised aperture keys, coerced to finite numbers.
function pickAperture(obj) {
  if (!obj || typeof obj !== 'object') return {};
  const out = {};
  APERTURE_KEYS.forEach((k) => {
    const v = Number(obj[k]);
    if (Number.isFinite(v)) out[k] = v;
  });
  return out;
}

if (!customElements.get('mab-locket-upload')) {
  customElements.define('mab-locket-upload', MabLocketUpload);
}
